import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Background,
  BackgroundVariant,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  useNodesState,
} from '@xyflow/react'
import type { Edge, Node, NodeProps, NodeTypes } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  ArrowRight,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  ChevronDown,
  CornerDownRight,
  Gift,
  Info,
  Zap,
  CloudCheck,
  CloudCog,
  Copy,
  CreditCard,
  Eye,
  FileText,
  GitFork,
  Globe2,
  GraduationCap,
  Handshake,
  HeartPulse,
  Image as ImageIcon,
  Landmark,
  LineChart,
  Link as LinkIcon,
  Mail,
  Megaphone,
  Package,
  PartyPopper,
  Paintbrush,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Tag,
  TicketPercent,
  Trash2,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Users,
  X,
} from 'lucide-react'
import {
  Banner,
  Button,
  Card,
  CurrencyField,
  DateField,
  FocusEditor,
  ImageUploadField,
  PercentageField,
  SegmentedControl,
  SelectField,
  StatusBadge,
  StepTrail,
  TextField,
  Toggle,
} from '@shared/design-system/ui'
import type { FocusSaveState, StatusTone, StepTrailItem } from '@shared/design-system/ui'
import { apiFetch } from '@shared/api/http'
import {
  affiliateInviteTokenFromUrl,
  affiliateInviteUrl,
  upsellSnippet,
} from '@shared/config/public-urls'
import type { Currency, PaymentMethod } from '@shared/contracts/enums'
import {
  pixelIdFormatError,
  pixelServerSideArmed,
  pixelTrackingReady,
} from '@shared/contracts/pixels'
import { PixelEventsTimeline } from '@shared/ui/PixelEventsTimeline'
import { extractApiFieldErrors } from '@shared/lib/forms/api-field-errors'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import {
  formatPercentageLabel,
  parsePercentage,
  percentageInputToFixed,
} from '@shared/lib/format/percent'
import { formatMoney } from '@shared/lib/format/currency'
import { toast } from '@shared/lib/toast/toast-store'
import { Money } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'
import { useDebounce } from '@shared/lib/use-debounce'
import {
  MAX_OFFERS_PER_PRODUCT,
  MAX_PLANS_PER_PRODUCT,
  canAddOffer,
  canAddPlan,
  editableOffers,
  editablePlans,
  getProductStatusPresentation,
  primaryOfferIdOf,
  sellablePlans,
} from '@entities/product'
import { paymentMethodLabel } from '@entities/sale'
import {
  FUNNEL_ROOT_ID,
  hasReadableEmailButtonContrast,
  ProductSchema,
  RecurrencePlanSchema,
  productTypeLabels,
} from '@entities/product/api/product.contract'
import type {
  Coupon,
  FunnelBranch,
  FunnelNode,
  Offer,
  OrderBump,
  Pixel,
  Product,
  ProductFeature,
  PriceModel,
  ProductType,
  RecurrencePlan,
} from '@entities/product/api/product.contract'
import {
  useActivateProductMutation,
  useArchiveProductMutation,
  useUpdateProductMutation,
} from '@entities/product/api/product.mutations'
import {
  parseProductResponse,
  parseProductsResponse,
  productKeys,
} from '@entities/product/api/product.queries'
import {
  coproductionKeys,
  coproductionStatusLabel,
  getAdminCoproductionForProduct,
  inviteToCoproducer,
  isLocalCoproducerId,
  syncCoproduction,
} from '@entities/partnership'
import type { CoproductionInvite, CoproductionStatus } from '@entities/partnership'
import { recurrencePlanRequestBody } from '../lib/recurrencePlanRequest'
import { newProductPixel } from '../lib/product-pixel'
import { couponAmountForCurrency, couponEndDateError } from '../lib/couponValidation'
import { plansToDeactivate, projectPrimaryPricing } from '../lib/single-primary-pricing'
import {
  allProductSteps,
  fallbackStepForHidden,
  featureDefinitions,
  getStepStatus,
  getStepTag,
} from '../model/product-steps'
import type { ProductStepId } from '../model/product-steps'
import {
  findOfferProductOption,
  findOptionForSelection,
  funnelSelectionsOf,
  getOfferProductOptions,
  synchronizeFunnelPlanPrices,
  type OfferProductOption,
} from '../lib/offerOptions'
import { emailVariables, projectEmailPreview } from '../lib/emailPreview'
import type { EmailPreviewContext } from '../lib/emailPreview'
import { computeSplitPreview } from '../lib/splitPreview'
import { CouponScopeNotice } from './CouponScopeNotice'
import {
  adminFeePreviewQuery,
  resolveFeePreviewForSave,
  usableFeePreview,
} from '../api/fee-preview'
import type { FeePreview } from '../api/fee-preview'
import { productCategoriesMatch, productCategoryLabel } from '../lib/product-category'
import {
  firstStepWithError,
  stepForFieldPath,
  stepHasFieldError,
} from '../lib/productEditorValidation'
import { validateStatementDescriptor } from '../lib/statementDescriptorValidation'
import { validateProductName } from '../lib/product-name'
import { validateProductPixels } from '../lib/pixel-validation'
import {
  actionRow,
  badge,
  basicAsideStack,
  basicDetailsGrid,
  basicMainStack,
  basicSupportGrid,
  billingModelCopy,
  billingModelDetail,
  billingModelIcon,
  billingModelTitle,
  checkoutSummaryCard,
  checkoutSummaryContent,
  checkoutSummaryGrid,
  checkoutSummaryHeader,
  checkoutSummaryItem,
  checkoutSummaryLabel,
  checkoutSummaryLink,
  checkoutSummaryValue,
  codeBox,
  compactCard,
  couponCard,
  couponCodeBlock,
  couponCodeValue,
  couponMetric,
  couponMetricLabel,
  couponMetricValue,
  couponPresetCard,
  couponPresetGrid,
  couponPreview,
  couponPreviewRow,
  couponPreviewTotal,
  couponStatusPill,
  couponSummaryGrid,
  editorRail,
  editorRailSave,
  editorRailTitle,
  editorStack,
  sentenceCard,
  sentenceText,
  sentenceChipWrap,
  sentenceChip,
  sentencePopover,
  sentencePopoverTitle,
  popoverNote,
  presetRow,
  presetChip,
  popoverUnitGrid,
  popoverOptionButton,
  popoverOptionTitle,
  popoverOptionMeta,
  popoverInlineRow,
  sentenceResolveRow,
  sentenceResolveText,
  sentenceHint,
  planNameHint,
  planNameInput,
  planFieldLabel,
  planCardIcon,
  trialSection,
  trialSectionHead,
  trialCardGrid,
  trialCard,
  trialCardTitle,
  trialCardCopy,
  trialTimeline,
  trialTimelineNode,
  trialTimelineDot,
  trialTimelineLabel,
  trialTimelineMeta,
  trialTimelineBar,
  trialFieldsRow,
  accordion,
  accordionHeader,
  accordionHeaderMain,
  accordionMeta,
  accordionChevron,
  accordionBody,
  checkoutPreviewLabel,
  checkoutPreviewList,
  checkoutPreviewRow,
  checkoutPreviewMain,
  checkoutPreviewRadio,
  checkoutPreviewName,
  checkoutPreviewMeta,
  checkoutPreviewPriceWrap,
  checkoutPreviewPrice,
  checkoutPreviewCycle,
  methodTag,
  methodTagSoon,
  methodTagSoonNote,
  emailDoc,
  emailDocBody,
  emailDocCard,
  emailDocFactLabel,
  emailDocFactRow,
  emailDocFacts,
  emailDocFactValue,
  emailDocFooter,
  emailDocFooterStrong,
  emailDocFooterText,
  emailDocHeader,
  emailDocSubject,
  emailDocText,
  emailFieldLabel,
  emailInactiveNote,
  emailMessageInput,
  emailPreviewHeader,
  emailPreviewHeading,
  emailThemeControls,
  emailThemePreviewLogo,
  emailThemeSwatch,
  emailThemeSwatchGrid,
  emailPreviewViewport,
  emailVarHint,
  featureCard,
  featureGrid,
  featureTitle,
  formGrid,
  globalPricingNotice,
  globalPricingNoticeIcon,
  globalPricingNoticeMeta,
  globalPricingNoticeText,
  funnelCanvas,
  funnelFlow,
  funnelLegend,
  funnelLegendMeta,
  funnelLegendTitle,
  funnelMetric,
  funnelMetricGrid,
  funnelMetricLabel,
  funnelMetricValue,
  funnelNode,
  funnelNodeBadge,
  funnelNodeInset,
  funnelNodeMeta,
  funnelNodePrice,
  funnelNodeTitle,
  funnelStageBackdrop,
  funnelStageBody,
  funnelStageClose,
  funnelStageFooter,
  funnelStageHeader,
  funnelStageIcon,
  funnelStagePanel,
  funnelStageSubtitle,
  funnelStageTitleStack,
  funnelStageTitle,
  inlineHeaderRow,
  inlineRow,
  linkCardContent,
  linkCardMain,
  modalDivider,
  muted,
  nodeTitle,
  offerModeCard,
  offerModeDescription,
  offerModeGrid,
  offerModeTitle,
  offerProductAvatar,
  offerProductCard,
  offerProductMeta,
  offerProductName,
  quickstartChoiceGroup,
  quickstartGroupLabel,
  rowCard,
  section,
  sectionCopy,
  sectionHeader,
  sectionTitle,
  segmentedField,
  segmentedLabel,
  tableLike,
  productTypeCard,
  productTypeCopy,
  productTypeGrid,
  productTypeIcon,
  productTypeTitle,
} from './product.css'
import {
  funnelBranchButton,
  funnelBranchRow,
  funnelBumpAdd,
  funnelBumpGroup,
  funnelBumpGroupLabel,
  funnelBumpRow,
  funnelBumpValue,
  funnelBumpValueSuffix,
  funnelHint,
  funnelPaymentNotice,
  funnelPaymentNoticeBody,
  funnelPaymentNoticeIcon,
  funnelPaymentNoticeStrong,
  funnelNodeHead,
  funnelNodeHint,
  funnelNodeHostTag,
  funnelNodeShell,
  offerModeDisabled,
  offerModeRecommended,
  offerModeSoon,
  offerPreviewAccept,
  offerPreviewBadge,
  offerPreviewCard,
  offerPreviewDecline,
  offerPreviewFrame,
  offerPreviewHeader,
  offerPreviewInterval,
  offerPreviewMedia,
  offerPreviewMediaImg,
  offerPreviewMediaPlaceholder,
  offerPreviewPriceFrom,
  offerPreviewPriceNow,
  offerPreviewPriceRow,
  offerPreviewSubtitle,
  offerPreviewTitle,
  snippetBox,
  snippetBoxHead,
  snippetBoxLabel,
  snippetCode,
  snippetCopyButton,
  snippetWarn,
} from './funnel.css'
import { PlanBenefitsInput } from './PlanBenefitsInput'

const checkoutStatusLabels: Record<Product['checkouts'][number]['status'], string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
}

const pixelProviders = [
  'facebook',
  'tiktok',
  'google_ads',
  'utmify',
] as const satisfies Pixel['provider'][]

const pixelProviderMeta = {
  facebook: {
    title: 'Meta / Facebook',
    shortTitle: 'Facebook',
    empty: 'Nenhum pixel Meta configurado.',
    description: 'Browser Pixel e Conversions API para eventos do produto.',
    icon: <Megaphone size={16} />,
  },
  tiktok: {
    title: 'TikTok',
    shortTitle: 'TikTok',
    empty: 'Nenhum pixel TikTok configurado.',
    description: 'Pixel e Events API para conversões do produto.',
    icon: <Play size={16} />,
  },
  google_ads: {
    title: 'Google Ads',
    shortTitle: 'Google Ads',
    empty: 'Nenhuma conversão Google Ads configurada.',
    description: 'Tag de conversão do Google Ads no navegador do comprador.',
    icon: <LineChart size={16} />,
  },
  utmify: {
    title: 'UTMify',
    shortTitle: 'UTMify',
    empty: 'Nenhuma credencial UTMify configurada.',
    description: 'Envia os pedidos (PIX aguardando, pago, reembolso) para o painel da UTMify',
    icon: <LinkIcon size={16} />,
  },
} satisfies Record<
  Pixel['provider'],
  {
    title: string
    shortTitle: string
    empty: string
    description: string
    icon: ReactNode
  }
>

const googleConversionEventOptions = [
  { value: 'purchase', label: 'Compra confirmada' },
  { value: 'begin_checkout', label: 'Visita ao checkout' },
]

function pixelSummary(pixel: Pixel) {
  if (pixel.provider === 'utmify') {
    return pixel.accessToken.trim()
      ? 'Credencial de API cadastrada.'
      : 'Cole a credencial de API para ativar.'
  }
  if (pixel.provider === 'google_ads') {
    return pixel.pixelId.trim() || 'Informe o Conversion ID para ativar.'
  }
  return pixel.pixelId.trim() || 'Informe o Pixel ID para ativar.'
}

export type ProductEditorProps = {
  open: boolean
  productId?: string
  adminProducerId?: string
  minuteBillingEnabled?: boolean
  approvalContext?: ProductEditorApprovalContext
  onClose: () => void
  onAfterClose?: () => void
  onOpenCheckoutBuilder: (productId: string, checkoutId?: string) => void
}

export type ProductEditorApprovalContext = {
  approvedBy: string
  reference: string
  approvedAt: string
}

type ProductEditorApiContextValue = {
  adminProducerId?: string
}

const ProductEditorApiContext = createContext<ProductEditorApiContextValue>({})

function useProductEditorProductsQuery() {
  const api = useContext(ProductEditorApiContext)
  const producerId = api.adminProducerId ?? ''
  return useQuery<Product[]>({
    queryKey: productKeys.adminList(producerId),
    queryFn: async () => {
      const data = await apiFetch<unknown>(`/admin/users/${producerId}/products`)
      return parseProductsResponse(data)
    },
    enabled: Boolean(producerId),
  })
}

const quickProductTypeOptions: Array<{
  label: string
  description: string
  value: ProductType
  icon: ReactNode
}> = [
  {
    label: 'Curso / Área de membros',
    description: 'Aulas, módulos e acesso por área.',
    value: 'course',
    icon: <GraduationCap size={21} />,
  },
  {
    label: 'Ebook / Arquivo',
    description: 'PDF, planilha ou material p/ download.',
    value: 'ebook',
    icon: <FileText size={21} />,
  },
  {
    label: 'Mentoria / Serviço',
    description: 'Sessões, consultorias, agendados.',
    value: 'service',
    icon: <Handshake size={21} />,
  },
]

type AffiliateToggleKey = 'manualApproval' | 'marketplace' | 'recurring' | 'buyerData'

const affiliateToggleOptions: Array<{
  key: AffiliateToggleKey
  label: string
  description: string
  icon: ReactNode
}> = [
  {
    key: 'manualApproval',
    label: 'Solicitações precisam de aprovação',
    description: 'Quem solicitar afiliação fica pendente antes de poder vender com comissão.',
    icon: <ShieldCheck size={16} />,
  },
  {
    key: 'marketplace',
    label: 'Exibir no marketplace',
    description: 'Afiliados encontram o produto publicamente; desligado, use o link privado.',
    icon: <Megaphone size={16} />,
  },
  {
    key: 'recurring',
    label: 'Comissão nas renovações',
    description: 'Mantém o afiliado no split das cobranças recorrentes desta assinatura.',
    icon: <RefreshCw size={16} />,
  },
]

const paymentMethods = [
  { label: 'Cartão', value: 'CREDIT_CARD' },
  { label: 'Pix', value: 'PIX', soon: true },
  { label: 'Boleto', value: 'BOLETO', soon: true },
] as const satisfies ReadonlyArray<{
  label: string
  value: PaymentMethod
  soon?: true
}>

const comingSoonPaymentMethods = new Set<PaymentMethod>(['PIX', 'BOLETO'])

function availablePaymentMethods(methods: readonly PaymentMethod[]): PaymentMethod[] {
  return methods.filter((method) => !comingSoonPaymentMethods.has(method))
}

const categoryOptions = [
  {
    label: 'Educação',
    value: 'Educação',
    description: 'Cursos, ebooks e treinamentos.',
    icon: <BookOpen size={19} />,
  },
  {
    label: 'Marketing',
    value: 'Marketing',
    description: 'Tráfego, vendas e crescimento.',
    icon: <Megaphone size={19} />,
  },
  {
    label: 'Negócios',
    value: 'Negócios',
    description: 'Gestão, carreira e operação.',
    icon: <BriefcaseBusiness size={19} />,
  },
  {
    label: 'Saúde e bem-estar',
    value: 'Saúde e bem-estar',
    description: 'Performance, hábitos e cuidado.',
    icon: <HeartPulse size={19} />,
  },
  {
    label: 'Finanças',
    value: 'Finanças',
    description: 'Investimentos, renda e controle.',
    icon: <Landmark size={19} />,
  },
]

const deliveryOptions = [
  { label: 'Digital', value: 'digital' },
  { label: 'Área de membros', value: 'members_area' },
  { label: 'URL externa', value: 'external_url' },
]

const stepIcons: Record<ProductStepId, ReactNode> = {
  basico: <Package size={14} />,
  recursos: <SlidersHorizontal size={14} />,
  preco: <Tag size={14} />,
  funnel: <GitFork size={14} />,
  tracking: <Megaphone size={14} />,
  global: <Globe2 size={14} />,
  afiliados: <Users size={14} />,
  cupons: <TicketPercent size={14} />,
  coproducao: <Handshake size={14} />,
  email: <Mail size={14} />,
  links: <LinkIcon size={14} />,
}

const FUNNEL_COLUMN = 360
const FUNNEL_ROW = 280
const FUNNEL_BRANCHES: FunnelBranch[] = ['accept', 'reject']
const HOSTED_OFFER_ENABLED: boolean = true

type FunnelFlowNodeData = {
  tone: 'checkout' | 'upsell' | 'downsell' | 'success'
  badge: string
  title: string
  meta?: string
  price?: string
  productLabel?: string
  bumps?: Array<{ id: string; title: string; value: string; valueSuffix?: string }>
  hostingLabel?: string
  hint?: string
  selected: boolean
  freeBranches?: FunnelBranch[]
  onAdd?: (branch: FunnelBranch) => void
  onAddBump?: () => void
  onEditBump?: (id: string) => void
}

type FunnelFlowNodeType = Node<FunnelFlowNodeData, 'funnelNode'>
type FunnelStageKind = 'order_bump' | FunnelNode['kind']
type FunnelStageDialogState = {
  kind: FunnelStageKind
  id?: string
  parentId?: string
  branch?: FunnelBranch
}
type FunnelStageDraft = {
  kind: FunnelStageKind
  id?: string
  previousKind?: FunnelStageKind
  parentId: string
  branch: FunnelBranch
  offerId: string
  price: Product['price']
  title: string
  subtext: string
  description: string
  acceptLabel: string
  rejectLabel: string
  mode: FunnelNode['mode']
  mediaUrl: string
  bumpImageUrl: string
  commissionable: boolean
  planId: string | null
}
type ProductEditorFieldErrors = Partial<Record<string, string>>

const feePreviewValidationError = 'A taxa efetiva precisa ser validada antes de salvar.'

const funnelStageLabels: Record<
  FunnelStageKind,
  { title: string; subtitle: string; badge: string }
> = {
  order_bump: {
    title: 'Order bump',
    subtitle: 'Oferta extra dentro do checkout',
    badge: 'Bump',
  },
  upsell: {
    title: 'Upsell',
    subtitle: 'Oferta logo após a compra aprovada',
    badge: 'Upsell',
  },
  downsell: {
    title: 'Downsell',
    subtitle: 'Oferta alternativa pós-compra',
    badge: 'Downsell',
  },
}

const funnelToneIcon: Record<FunnelFlowNodeData['tone'], ReactNode> = {
  checkout: <ShoppingCart size={14} />,
  upsell: <TrendingUp size={14} color="#5B7BFF" />,
  downsell: <TrendingDown size={14} color="#E6A817" />,
  success: <PartyPopper size={14} />,
}

function getBranchChild(nodes: FunnelNode[], parentId: string, branch: FunnelBranch) {
  return nodes.find((node) => node.parentId === parentId && node.branch === branch)
}

function getFreeBranches(nodes: FunnelNode[], parentId: string, branches: FunnelBranch[]) {
  return branches.filter((branch) => !getBranchChild(nodes, parentId, branch))
}

function collectDescendantIds(nodes: FunnelNode[], rootId: string): string[] {
  return nodes
    .filter((node) => node.parentId === rootId)
    .flatMap((child) => [child.id, ...collectDescendantIds(nodes, child.id)])
}

function sanitizeFunnelNodes(nodes: FunnelNode[]): FunnelNode[] {
  const ids = new Set(nodes.map((node) => node.id))
  const used = new Set<string>()
  const kept: FunnelNode[] = []
  for (const node of nodes) {
    const parentExists = node.parentId === FUNNEL_ROOT_ID || ids.has(node.parentId)
    const slot = `${node.parentId}:${node.branch}`
    if (node.parentId === node.id || !parentExists || used.has(slot)) continue
    used.add(slot)
    kept.push(node)
  }
  return kept
}

type FunnelLayout = {
  positions: Map<string, { x: number; y: number }>
  maxDepth: number
  lanes: number
}

function layoutFunnelTree(nodes: FunnelNode[]): FunnelLayout {
  const positions = new Map<string, { x: number; y: number }>()
  let lane = 0
  let maxDepth = 0

  function place(id: string, depth: number): number {
    maxDepth = Math.max(maxDepth, depth)
    const childLanes: number[] = []
    for (const branch of FUNNEL_BRANCHES) {
      const child = getBranchChild(nodes, id, branch)
      if (child) childLanes.push(place(child.id, depth + 1))
    }
    const laneValue =
      childLanes.length > 0
        ? childLanes.reduce((sum, value) => sum + value, 0) / childLanes.length
        : lane++
    positions.set(id, { x: depth * FUNNEL_COLUMN, y: laneValue * FUNNEL_ROW })
    return laneValue
  }

  place(FUNNEL_ROOT_ID, 0)
  return { positions, maxDepth, lanes: Math.max(lane, 1) }
}

function FunnelFlowNode({ data }: NodeProps<FunnelFlowNodeType>) {
  const isCheckout = data.tone === 'checkout'
  const isSuccess = data.tone === 'success'
  const freeBranches = data.freeBranches ?? []

  return (
    <div
      className={`${funnelNode({ tone: data.tone, selected: data.selected })} ${funnelNodeShell}`}
    >
      {isCheckout ? null : <Handle type="target" position={Position.Left} />}
      <div className={funnelNodeHead}>
        <span className={funnelNodeBadge({ tone: data.tone })}>
          {funnelToneIcon[data.tone]}
          {data.badge}
        </span>
        {data.hostingLabel ? <span className={funnelNodeHostTag}>{data.hostingLabel}</span> : null}
      </div>
      <div className={funnelNodeTitle}>{data.title}</div>
      {data.productLabel || data.price ? (
        <div className={funnelNodeInset}>
          {data.productLabel ? <span className={funnelNodeMeta}>{data.productLabel}</span> : null}
          {data.price ? <strong className={funnelNodePrice}>{data.price}</strong> : null}
        </div>
      ) : null}
      {isCheckout ? (
        <div className={funnelBumpGroup}>
          <span className={funnelBumpGroupLabel}>
            <ShoppingCart size={11} />
            Order bumps · no checkout
          </span>
          {(data.bumps ?? []).map((bump) => (
            <button
              key={bump.id}
              type="button"
              className={funnelBumpRow}
              onClick={(event) => {
                event.stopPropagation()
                data.onEditBump?.(bump.id)
              }}
            >
              <strong>{bump.title}</strong>
              <span className={funnelBumpValue}>
                <strong>{bump.value}</strong>
                {bump.valueSuffix ? (
                  <span className={funnelBumpValueSuffix}>{bump.valueSuffix}</span>
                ) : null}
              </span>
            </button>
          ))}
          <button
            type="button"
            className={funnelBumpAdd}
            onClick={(event) => {
              event.stopPropagation()
              data.onAddBump?.()
            }}
          >
            <Plus size={14} />
            Adicionar order bump
          </button>
        </div>
      ) : null}
      {data.meta ? <div className={funnelNodeMeta}>{data.meta}</div> : null}
      {data.hint ? (
        <span className={funnelNodeHint}>
          <Pencil size={11} />
          {data.hint}
        </span>
      ) : null}
      {isSuccess ? null : <Handle type="source" position={Position.Right} />}
      {freeBranches.length > 0 ? (
        <div className={funnelBranchRow}>
          {freeBranches.includes('accept') ? (
            <button
              type="button"
              className={funnelBranchButton({ branch: 'accept' })}
              aria-label={isCheckout ? 'Adicionar primeiro upsell' : 'Adicionar upsell'}
              onClick={(event) => {
                event.stopPropagation()
                data.onAdd?.('accept')
              }}
            >
              <TrendingUp size={13} />
              {isCheckout ? 'primeiro upsell' : 'upsell'}
            </button>
          ) : null}
          {freeBranches.includes('reject') ? (
            <button
              type="button"
              className={funnelBranchButton({ branch: 'reject' })}
              aria-label="Adicionar downsell"
              onClick={(event) => {
                event.stopPropagation()
                data.onAdd?.('reject')
              }}
            >
              <TrendingDown size={13} />
              downsell
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

const funnelNodeTypes = {
  funnelNode: FunnelFlowNode,
} satisfies NodeTypes

const acceptedEdge = {
  style: { stroke: '#04D361', strokeWidth: 3 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#04D361' },
  labelStyle: { fill: '#04D361', fontWeight: 700 },
  labelBgStyle: { fill: 'transparent' },
} satisfies Partial<Edge>

const refusedEdge = {
  style: { stroke: '#94A3B8', strokeWidth: 3, strokeDasharray: '8 7' },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#94A3B8' },
  labelStyle: { fill: '#94A3B8', fontWeight: 700 },
  labelBgStyle: { fill: 'transparent' },
} satisfies Partial<Edge>

const terminalEdge = {
  style: { stroke: '#CBD5E1', strokeWidth: 2, strokeDasharray: '5 6' },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#CBD5E1' },
  labelStyle: { fill: '#64748B', fontWeight: 700 },
  labelBgStyle: { fill: 'transparent' },
} satisfies Partial<Edge>

function makeId(prefix: string) {
  const randomId =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.round(Math.random() * 100_000)}`
  return `${prefix}_${randomId}`
}

function money(amount: string, currency = 'BRL') {
  return { amount, currency: currency as Product['price']['currency'] }
}

function buildUpsellSnippet(offerId: string) {
  return upsellSnippet(offerId)
}

function primaryOfferIdFor(product: Product) {
  return primaryOfferIdOf(product)
}

function syncPrimaryOfferPrice(product: Product, price: Product['price']): Product {
  const primaryOfferId = primaryOfferIdFor(product)
  const offers =
    product.offers.length > 0
      ? product.offers.map((offer, index) =>
          offer.id === primaryOfferId || (primaryOfferId.length === 0 && index === 0)
            ? { ...offer, price }
            : offer,
        )
      : [
          {
            id: makeId('offer'),
            name: 'Oferta principal',
            price,
            status: 'ACTIVE' as const,
          },
        ]

  return {
    ...product,
    price,
    offers,
    links: {
      ...product.links,
      primaryOfferId: primaryOfferId || offers[0]?.id || '',
    },
  }
}

function applyBillingModel(product: Product, priceModel: PriceModel): Product {
  const hasZeroPrice = product.price.amount === '0.000'
  const restoredPrice = hasZeroPrice ? money('97.000', product.price.currency) : product.price
  const productPaymentMethods = availablePaymentMethods(product.paymentMethods)
  const next = syncPrimaryOfferPrice(
    {
      ...product,
      priceModel,
      features: { ...product.features, global: true, recurrence: priceModel === 'subscription' },
      paymentMethods:
        priceModel === 'subscription'
          ? ['CREDIT_CARD']
          : productPaymentMethods.length > 0
            ? productPaymentMethods
            : ['CREDIT_CARD'],
    },
    restoredPrice,
  )

  return priceModel === 'subscription' ? withSubscriptionDefaults(next) : next
}

function formatMoneyValue(value: Product['price']) {
  return formatMoney(value.amount, value.currency)
}

function planPriceLabel(
  plan: Pick<RecurrencePlan, 'value' | 'billingPeriodUnit' | 'billingPeriodCount'>,
) {
  return `${formatMoneyValue(plan.value)}/${intervalLabel(plan)}`
}

function mainProductPriceLabel(product: Product) {
  if (product.priceModel !== 'subscription') return formatMoneyValue(product.price)
  const activePlans = sellablePlans(product)
  if (activePlans.length === 0) return formatMoneyValue(product.price)
  const cheapest = activePlans.reduce((best, plan) =>
    Number(plan.value.amount) < Number(best.value.amount) ? plan : best,
  )
  return activePlans.length === 1
    ? planPriceLabel(cheapest)
    : `a partir de ${planPriceLabel(cheapest)}`
}

function mainProductMeta(product: Product) {
  const category = productCategoryLabel(product.category)
  if (product.priceModel !== 'subscription') return category
  const activePlans = sellablePlans(product)
  if (activePlans.length <= 1) return `${category} · Assinatura`
  return `${category} · Assinatura · ${activePlans.length} planos`
}

function intervalLabel(plan: Pick<RecurrencePlan, 'billingPeriodUnit' | 'billingPeriodCount'>) {
  const singular: Record<RecurrencePlan['billingPeriodUnit'], string> = {
    MINUTE: 'minuto',
    DAY: 'dia',
    WEEK: 'semana',
    MONTH: 'mês',
    YEAR: 'ano',
  }
  const plural: Record<RecurrencePlan['billingPeriodUnit'], string> = {
    MINUTE: 'minutos',
    DAY: 'dias',
    WEEK: 'semanas',
    MONTH: 'meses',
    YEAR: 'anos',
  }
  return plan.billingPeriodCount > 1
    ? `${plan.billingPeriodCount} ${plural[plan.billingPeriodUnit]}`
    : singular[plan.billingPeriodUnit]
}

const recurrenceUnitOptions: Array<{
  label: string
  value: RecurrencePlan['billingPeriodUnit']
}> = [
  { label: 'Minutos', value: 'MINUTE' },
  { label: 'Dias', value: 'DAY' },
  { label: 'Semanas', value: 'WEEK' },
  { label: 'Meses', value: 'MONTH' },
  { label: 'Anos', value: 'YEAR' },
]

const recurrencePresetOptions = [
  { label: 'Mensal', unit: 'MONTH', count: 1 },
  { label: 'Trimestral', unit: 'MONTH', count: 3 },
  { label: 'Anual', unit: 'YEAR', count: 1 },
  { label: 'Semanal', unit: 'WEEK', count: 1 },
] satisfies Array<{
  label: string
  unit: RecurrencePlan['billingPeriodUnit']
  count: number
}>
const defaultRecurrencePreset = recurrencePresetOptions[0]!

function createRecurrencePlan(
  product: Product,
  preset: (typeof recurrencePresetOptions)[number] = defaultRecurrencePreset,
): RecurrencePlan {
  return {
    id: makeId('plan'),
    name: `Plano ${preset.label.toLowerCase()}`,
    benefits: [],
    value: product.price,
    billingPeriodUnit: preset.unit,
    billingPeriodCount: preset.count,
    billingAnchorType: 'IMMEDIATE',
    billingAnchorDayOfMonth: null,
    trialEnabled: false,
    trialType: 'WITH_PAYMENT',
    trialDays: 0,
    trialPrice: money('0.000', product.price.currency),
    retryAttempts: 3,
    retryDelayDays: 3,
    graceEnabled: false,
    graceDays: 0,
    active: true,
  }
}

function withSubscriptionDefaults(product: Product): Product {
  if (product.recurrencePlans.length > 0) {
    return product
  }
  return {
    ...product,
    recurrencePlans: [createRecurrencePlan(product)],
  }
}

function recurrenceUnitNoun(unit: RecurrencePlan['billingPeriodUnit'], count: number) {
  const singular: Record<RecurrencePlan['billingPeriodUnit'], string> = {
    MINUTE: 'minuto',
    DAY: 'dia',
    WEEK: 'semana',
    MONTH: 'mês',
    YEAR: 'ano',
  }
  const plural: Record<RecurrencePlan['billingPeriodUnit'], string> = {
    MINUTE: 'minutos',
    DAY: 'dias',
    WEEK: 'semanas',
    MONTH: 'meses',
    YEAR: 'anos',
  }
  return count === 1 ? singular[unit] : plural[unit]
}

function recurrenceCycleLabel(plan: RecurrencePlan) {
  const count = Math.max(1, plan.billingPeriodCount)
  const noun = recurrenceUnitNoun(plan.billingPeriodUnit, count)
  if (count === 1) {
    if (plan.billingPeriodUnit === 'MINUTE') return 'Todo minuto'
    if (plan.billingPeriodUnit === 'DAY') return 'Todo dia'
    if (plan.billingPeriodUnit === 'WEEK') return 'Toda semana'
    if (plan.billingPeriodUnit === 'MONTH') return 'Todo mês'
    return 'Todo ano'
  }
  return `A cada ${count} ${noun}`
}

function recurrenceAnchorLabel(plan: RecurrencePlan) {
  if (plan.billingAnchorType !== 'CALENDAR') return 'Data da compra'
  return `Dia ${plan.billingAnchorDayOfMonth ?? 1} do mês`
}

function recurrenceTrialLabel(plan: RecurrencePlan) {
  if (!plan.trialEnabled || plan.trialDays <= 0) return 'Sem trial'
  if (plan.trialType === 'PAID') {
    return `${plan.trialDays} dias por ${formatBuyerPrice(plan.trialPrice)}`
  }
  return `${plan.trialDays} dias com cartão`
}

function recurrenceRecoveryLabel(plan: RecurrencePlan) {
  const retry =
    plan.retryAttempts === 0
      ? 'sem retentativa'
      : `${plan.retryAttempts} retentativa${plan.retryAttempts === 1 ? '' : 's'}`
  const grace =
    plan.graceEnabled && plan.graceDays > 0 ? `${plan.graceDays} dias de grace` : 'sem grace'
  return `${retry} · ${grace}`
}

function detectMediaKind(url: string): 'video' | 'image' | 'empty' {
  const value = url.trim().toLowerCase()
  if (!value) return 'empty'
  if (/youtube|youtu\.be|vimeo|vturb|panda|converteai|player|\/embed\/|\.mp4|\.webm/.test(value)) {
    return 'video'
  }
  return 'image'
}

function formatBuyerPrice(value: Product['price']) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: value.currency,
  }).format(Money.fromMonetaryValue(value).toNumber())
}

const couponPresetOptions = [
  {
    label: 'Boas-vindas',
    description: '10% para primeira campanha',
    code: 'BEMVINDO10',
    type: 'percent',
    percent: 10,
    amount: '10.000',
    usageLimit: 100,
    durationDays: null,
    icon: <TicketPercent size={18} />,
  },
  {
    label: 'Lote relâmpago',
    description: '15% por tempo curto',
    code: 'RELAMPAGO15',
    type: 'percent',
    percent: 15,
    amount: '15.000',
    usageLimit: 50,
    durationDays: 7,
    icon: <TrendingDown size={18} />,
  },
  {
    label: 'Valor fixo',
    description: 'Desconto nominal controlado',
    code: 'DESCONTO50',
    type: 'fixed',
    percent: 0,
    amount: '50.000',
    usageLimit: 100,
    durationDays: null,
    icon: <Tag size={18} />,
  },
] satisfies Array<{
  label: string
  description: string
  code: string
  type: Coupon['type']
  percent: number
  amount: string
  usageLimit: number
  durationDays: number | null
  icon: ReactNode
}>

const defaultCouponPreset = couponPresetOptions[0]!
const COUPON_OPEN_START = '1970-01-01'
const COUPON_OPEN_END = '9999-12-31'

function couponPresetDetail(
  preset: (typeof couponPresetOptions)[number],
  currency: Product['price']['currency'],
) {
  const discount =
    preset.type === 'percent'
      ? formatPercentageLabel(preset.percent)
      : formatBuyerPrice(money(preset.amount, currency))
  const window = preset.durationDays ? `por ${preset.durationDays} dias` : 'sem prazo'
  return `${discount} ${window}`
}

function isoDateFromLocal(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function normalizeCouponDateInput(value: string) {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return isoDateFromLocal(date)
}

function dateFromIsoDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return new Date()
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

function addDaysToIsoDate(value: string, days: number) {
  const iso = normalizeCouponDateInput(value) || isoDateFromLocal(new Date())
  const date = dateFromIsoDate(iso)
  date.setDate(date.getDate() + days)
  return isoDateFromLocal(date)
}

function defaultCouponDates(durationDays = 30) {
  const startsAt = isoDateFromLocal(new Date())
  return { startsAt, endsAt: addDaysToIsoDate(startsAt, durationDays) }
}

function couponPresetDates(durationDays: number | null) {
  return durationDays === null ? { startsAt: '', endsAt: '' } : defaultCouponDates(durationDays)
}

function isOpenCouponStart(value: string) {
  const iso = normalizeCouponDateInput(value)
  return !iso || iso <= COUPON_OPEN_START
}

function isOpenCouponEnd(value: string) {
  const iso = normalizeCouponDateInput(value)
  return !iso || iso >= COUPON_OPEN_END
}

function couponDateWindow(coupon: Coupon) {
  const normalizedStart = normalizeCouponDateInput(coupon.startsAt)
  const normalizedEnd = normalizeCouponDateInput(coupon.endsAt)
  const startsAt = normalizedStart && !isOpenCouponStart(normalizedStart) ? normalizedStart : ''
  const endsAt = normalizedEnd && !isOpenCouponEnd(normalizedEnd) ? normalizedEnd : ''
  return {
    startsAt,
    endsAt: startsAt && endsAt && endsAt < startsAt ? startsAt : endsAt,
  }
}

function couponStorageWindow(coupon: Coupon) {
  const { startsAt, endsAt } = couponDateWindow(coupon)
  return {
    startsAt: startsAt || COUPON_OPEN_START,
    endsAt: endsAt || COUPON_OPEN_END,
  }
}

function sanitizeCouponCode(value: string) {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 25)
}

function formatCouponDate(value: string) {
  const iso = normalizeCouponDateInput(value)
  if (!iso) return 'Sem data'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(dateFromIsoDate(iso))
}

function couponDiscountLabel(coupon: Coupon, currency: Product['price']['currency']) {
  if (coupon.type === 'percent') return formatPercentageLabel(coupon.percent)
  return formatBuyerPrice(couponAmountForCurrency(coupon, currency))
}

function couponWindowLabel(coupon: Coupon) {
  const { startsAt, endsAt } = couponDateWindow(coupon)
  if (!startsAt && !endsAt) return 'Sem prazo'
  if (startsAt && !endsAt) return `A partir de ${formatCouponDate(startsAt)}`
  if (!startsAt && endsAt) return `Até ${formatCouponDate(endsAt)}`
  return `${formatCouponDate(startsAt)} até ${formatCouponDate(endsAt)}`
}

function couponStatus(coupon: Coupon) {
  const { startsAt, endsAt } = couponDateWindow(coupon)
  const today = isoDateFromLocal(new Date())
  if (!coupon.active) {
    return { label: 'Inativo', tone: 'inactive' as const }
  }
  if (startsAt && today < startsAt) {
    return { label: 'Agendado', tone: 'scheduled' as const }
  }
  if (endsAt && today > endsAt) {
    return { label: 'Expirado', tone: 'expired' as const }
  }
  return { label: 'Ativo', tone: 'active' as const }
}

function moneyFromNumber(value: number, currency: Product['price']['currency']) {
  return money(new Money(Math.max(0, value), currency).toAmountString(), currency)
}

function couponPricePreview(product: Product, coupon: Coupon) {
  const base = Money.fromMonetaryValue(product.price).toNumber()
  if (base <= 0) {
    return {
      discount: 'Sem preço',
      total: 'Defina a oferta principal',
      note: 'A prévia aparece depois que o produto tem preço.',
    }
  }

  const couponAmount = couponAmountForCurrency(coupon, product.price.currency)
  const rawDiscount =
    coupon.type === 'percent'
      ? (base * clampNumber(coupon.percent, 0, 100)) / 100
      : Money.fromMonetaryValue(couponAmount).toNumber()
  const discount = Math.max(0, rawDiscount)
  const final = Math.max(0, base - discount)

  return {
    discount: formatBuyerPrice(moneyFromNumber(Math.min(discount, base), product.price.currency)),
    total: formatBuyerPrice(moneyFromNumber(final, product.price.currency)),
    note: discount >= base ? 'Revise: este desconto zera a oferta principal.' : '',
  }
}

function HostedOfferPreview({
  title,
  subtext,
  mediaUrl,
  acceptLabel,
  rejectLabel,
  fullPrice,
  nowPrice,
  plan,
  showFrom,
}: {
  title: string
  subtext: string
  mediaUrl: string
  acceptLabel: string
  rejectLabel: string
  fullPrice: Product['price']
  nowPrice: Product['price']
  plan: RecurrencePlan | null
  showFrom: boolean
}) {
  const [failedMediaUrl, setFailedMediaUrl] = useState<string | null>(null)

  const media = detectMediaKind(mediaUrl)
  const fullAmount = Money.fromMonetaryValue(fullPrice).toNumber()
  const nowAmount = Money.fromMonetaryValue(nowPrice).toNumber()
  const hasDiscount = !plan && showFrom && fullAmount > nowAmount
  const showImage = media === 'image' && failedMediaUrl !== mediaUrl

  return (
    <div className={offerPreviewCard}>
      <span className={offerPreviewHeader}>
        <Eye size={13} />
        Prévia · o que o comprador vê
      </span>
      <div className={offerPreviewFrame}>
        <span className={offerPreviewBadge}>{plan ? 'Assinatura' : 'Oferta única'}</span>
        <strong className={offerPreviewTitle}>{title.trim() || 'Chamada da oferta'}</strong>
        {subtext.trim() ? <span className={offerPreviewSubtitle}>{subtext}</span> : null}
        <div className={offerPreviewMedia}>
          {showImage ? (
            <img
              className={offerPreviewMediaImg}
              src={mediaUrl}
              alt=""
              onError={() => setFailedMediaUrl(mediaUrl)}
            />
          ) : media === 'video' ? (
            <span className={offerPreviewMediaPlaceholder}>
              <Play size={20} />
              Vídeo da oferta
            </span>
          ) : (
            <span className={offerPreviewMediaPlaceholder}>
              <ImageIcon size={20} />
              Imagem ou vídeo
            </span>
          )}
        </div>
        <div className={offerPreviewPriceRow}>
          {hasDiscount ? (
            <span className={offerPreviewPriceFrom}>{formatBuyerPrice(fullPrice)}</span>
          ) : null}
          <span className={offerPreviewPriceNow}>
            {plan ? formatBuyerPrice(plan.value) : formatBuyerPrice(nowPrice)}
            {plan ? <span className={offerPreviewInterval}>/{intervalLabel(plan)}</span> : null}
          </span>
        </div>
        <span className={offerPreviewAccept}>{acceptLabel.trim() || 'Sim, quero'}</span>
        <span className={offerPreviewDecline}>{rejectLabel.trim() || 'Não, obrigado'}</span>
      </div>
    </div>
  )
}

function GlobalPricingNotice({ product }: { product: Product }) {
  const currency = product.price.currency

  return (
    <div className={globalPricingNotice}>
      <span className={globalPricingNoticeIcon} aria-hidden="true">
        <Globe2 size={18} />
      </span>
      <div className={globalPricingNoticeText}>
        <span className={featureTitle}>Mercado global · preço base em {currency}</span>
        <span className={globalPricingNoticeMeta}>
          No checkout, a VendePay converte em tempo real para a moeda do comprador. O valor
          liquidado pode variar levemente por câmbio.
        </span>
      </div>
    </div>
  )
}

function EditableChip({
  label,
  ariaLabel,
  withChevron = false,
  children,
}: {
  label: ReactNode
  ariaLabel: string
  withChevron?: boolean
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    function handlePointer(event: MouseEvent) {
      if (
        wrapRef.current &&
        event.target instanceof globalThis.Node &&
        !wrapRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  return (
    <span className={sentenceChipWrap} ref={wrapRef}>
      <button
        type="button"
        className={sentenceChip({ open })}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
        {withChevron ? <ChevronDown size={14} /> : null}
      </button>
      {open ? (
        <span className={sentencePopover} role="dialog" aria-label={ariaLabel}>
          {children(() => setOpen(false))}
        </span>
      ) : null}
    </span>
  )
}

function recurrenceResolveSentence(plan: RecurrencePlan) {
  const price = formatBuyerPrice(plan.value)
  const cycle = recurrenceCycleLabel(plan).toLowerCase()
  if (plan.billingPeriodUnit !== 'MONTH') {
    return `Cobrança de ${price}, ${cycle}.`
  }
  if (plan.billingAnchorType === 'CALENDAR') {
    return `Cobrança de ${price}, ${cycle}, todo dia ${plan.billingAnchorDayOfMonth ?? 1}. Meses sem esse dia cobram no último dia.`
  }
  return `Cobrança de ${price}, ${cycle}, na mesma data da compra.`
}

const unitSingularLabels: Record<RecurrencePlan['billingPeriodUnit'], string> = {
  MINUTE: 'Minuto',
  DAY: 'Dia',
  WEEK: 'Semana',
  MONTH: 'Mês',
  YEAR: 'Ano',
}

function recurrencePeriodSuffix(plan: RecurrencePlan) {
  const noun = recurrenceUnitNoun(plan.billingPeriodUnit, plan.billingPeriodCount)
  return plan.billingPeriodCount === 1 ? `/${noun}` : `/${plan.billingPeriodCount} ${noun}`
}

function renderCadencePopover(
  plan: RecurrencePlan,
  updatePlan: (planId: string, input: Partial<RecurrencePlan>) => void,
  updatePlanUnit: (plan: RecurrencePlan, unit: RecurrencePlan['billingPeriodUnit']) => void,
  minuteBillingEnabled: boolean,
  close: () => void,
) {
  return (
    <>
      <span className={sentencePopoverTitle}>Repetir a cada</span>
      <div className={popoverInlineRow}>
        <TextField
          type="number"
          min={1}
          max={plan.billingPeriodUnit === 'MINUTE' ? 10 : 365}
          value={plan.billingPeriodCount}
          onChange={(event) =>
            updatePlan(plan.id, {
              billingPeriodCount: clampIntegerInput(
                event.target.value,
                1,
                plan.billingPeriodUnit === 'MINUTE' ? 10 : 365,
              ),
            })
          }
        />
        <span className={sentenceResolveText}>vez(es) o período abaixo</span>
      </div>
      <div className={popoverUnitGrid}>
        {recurrenceUnitOptions
          .filter(
            (option) =>
              option.value !== 'MINUTE' ||
              minuteBillingEnabled ||
              plan.billingPeriodUnit === 'MINUTE',
          )
          .map((option) => (
            <button
              key={option.value}
              type="button"
              className={popoverOptionButton({ active: option.value === plan.billingPeriodUnit })}
              onClick={() => updatePlanUnit(plan, option.value)}
            >
              {unitSingularLabels[option.value]}
            </button>
          ))}
      </div>
      <Button size="sm" onClick={close}>
        Pronto
      </Button>
    </>
  )
}

function renderAnchorPopover(
  plan: RecurrencePlan,
  updatePlan: (planId: string, input: Partial<RecurrencePlan>) => void,
  close: () => void,
) {
  return (
    <>
      <span className={sentencePopoverTitle}>Quando renova?</span>
      <button
        type="button"
        className={popoverOptionButton({ active: plan.billingAnchorType === 'IMMEDIATE' })}
        onClick={() =>
          updatePlan(plan.id, { billingAnchorType: 'IMMEDIATE', billingAnchorDayOfMonth: null })
        }
      >
        <span className={popoverOptionTitle}>Na data da compra</span>
        <span className={popoverOptionMeta}>Comprou dia 12, renova todo dia 12.</span>
      </button>
      <button
        type="button"
        className={popoverOptionButton({ active: plan.billingAnchorType === 'CALENDAR' })}
        onClick={() =>
          updatePlan(plan.id, {
            billingAnchorType: 'CALENDAR',
            billingAnchorDayOfMonth: plan.billingAnchorDayOfMonth ?? 1,
          })
        }
      >
        <span className={popoverOptionTitle}>Dia fixo do mês</span>
        <span className={popoverOptionMeta}>Todo mundo renova no mesmo dia.</span>
      </button>
      {plan.billingAnchorType === 'CALENDAR' ? (
        <div className={popoverInlineRow}>
          <span className={sentenceResolveText}>Renova todo dia</span>
          <TextField
            type="number"
            min={1}
            max={30}
            value={plan.billingAnchorDayOfMonth ?? 1}
            onChange={(event) =>
              updatePlan(plan.id, {
                billingAnchorDayOfMonth: clampIntegerInput(event.target.value, 1, 30),
              })
            }
          />
        </div>
      ) : null}
      <div className={popoverNote}>
        <Info size={13} />
        <span>Meses sem esse dia (ex.: 31 em fevereiro) cobram no último dia do mês.</span>
      </div>
      <Button size="sm" onClick={close}>
        Pronto
      </Button>
    </>
  )
}

function renderTrialTimeline(plan: RecurrencePlan) {
  if (!plan.trialEnabled || plan.trialDays <= 0) {
    return (
      <div className={popoverInlineRow}>
        <Zap size={15} />
        <span className={sentenceResolveText}>
          Sem período de teste: o comprador paga {formatBuyerPrice(plan.value)} já na primeira
          cobrança.
        </span>
      </div>
    )
  }
  const firstMeta =
    plan.trialType === 'PAID' ? `Paga ${formatBuyerPrice(plan.trialPrice)}` : 'Grátis'
  return (
    <div className={trialTimeline}>
      <div className={trialTimelineNode}>
        <span className={trialTimelineDot({ tone: 'start' })}>0</span>
        <span className={trialTimelineLabel}>Compra</span>
        <span className={trialTimelineMeta}>{firstMeta}</span>
      </div>
      <div className={trialTimelineBar} />
      <div className={trialTimelineNode}>
        <span className={trialTimelineDot({ tone: 'mid' })}>D{plan.trialDays}</span>
        <span className={trialTimelineLabel}>1ª cobrança</span>
        <span className={trialTimelineMeta}>{formatBuyerPrice(plan.value)}</span>
      </div>
      <div className={trialTimelineBar} />
      <div className={trialTimelineNode}>
        <span className={trialTimelineDot({ tone: 'end' })}>
          <RefreshCw size={13} />
        </span>
        <span className={trialTimelineLabel}>Renova</span>
        <span className={trialTimelineMeta}>
          {formatBuyerPrice(plan.value)} {recurrencePeriodSuffix(plan)}
        </span>
      </div>
    </div>
  )
}

function PlanRecoveryAccordion({
  plan,
  updatePlan,
}: {
  plan: RecurrencePlan
  updatePlan: (planId: string, input: Partial<RecurrencePlan>) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className={accordion}>
      <button
        type="button"
        className={accordionHeader}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={accordionHeaderMain}>
          <SlidersHorizontal size={15} />
          Cobrança &amp; falhas
          <span className={accordionMeta}>· {recurrenceRecoveryLabel(plan)}</span>
        </span>
        <span className={accordionChevron({ open })}>
          <ChevronDown size={17} />
        </span>
      </button>
      {open ? (
        <div className={accordionBody}>
          <div className={editorStack}>
            <div className={trialFieldsRow}>
              <TextField
                label="Retentativas se a cobrança falhar"
                type="number"
                min={0}
                max={10}
                value={plan.retryAttempts}
                onChange={(event) =>
                  updatePlan(plan.id, {
                    retryAttempts: clampIntegerInput(event.target.value, 0, 10),
                  })
                }
              />
              <TextField
                label="Intervalo entre tentativas"
                type="number"
                min={1}
                max={30}
                value={plan.retryDelayDays}
                rightSlot={<span>dias</span>}
                onChange={(event) =>
                  updatePlan(plan.id, {
                    retryDelayDays: clampIntegerInput(event.target.value, 1, 30),
                  })
                }
              />
            </div>
            <div className={featureCard}>
              <div>
                <span className={featureTitle}>Manter acesso durante as tentativas</span>
                <p className={muted}>
                  {plan.graceEnabled ? `${plan.graceDays} dias de carência` : 'Carência desativada'}
                </p>
              </div>
              <Toggle
                checked={plan.graceEnabled}
                onChange={(graceEnabled) =>
                  updatePlan(plan.id, {
                    graceEnabled,
                    graceDays: graceEnabled ? Math.max(1, plan.graceDays || 3) : 0,
                  })
                }
                ariaLabel={`Carência do ${plan.name || 'plano'}`}
              />
            </div>
            {plan.graceEnabled ? (
              <div className={trialFieldsRow}>
                <TextField
                  label="Dias de carência"
                  type="number"
                  min={1}
                  max={60}
                  value={plan.graceDays}
                  onChange={(event) =>
                    updatePlan(plan.id, {
                      graceDays: clampIntegerInput(event.target.value, 1, 60),
                    })
                  }
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function getDiscountedPrice(price: Product['price'], discount: string) {
  const discountPercent = clampNumber(parseDecimal(discount), 0, 100)
  const multiplier = Math.max(0, 1 - discountPercent / 100)
  const value = Money.fromMonetaryValue(price).toNumber() * multiplier
  return money(new Money(value, price.currency).toAmountString(), price.currency)
}

function sumSameCurrency(base: Product['price'], values: Product['price'][]) {
  const total = values.reduce((current, value) => {
    if (value.currency !== base.currency) return current
    return current.add(Money.fromMonetaryValue(value))
  }, Money.fromMonetaryValue(base))

  return total.toMonetaryValue()
}

function normalizeDecimalInput(value: string) {
  const normalized = value.replace(',', '.').replace(/[^\d.]/g, '')
  const [integerRaw = '', ...decimalParts] = normalized.split('.')
  const integer = integerRaw.replace(/^0+(?=\d)/, '') || '0'
  const decimal = decimalParts.join('').slice(0, 3)
  return decimal.length > 0 ? `${integer}.${decimal}` : integer
}

function toLedgerAmount(value: string, currency: Product['price']['currency']) {
  const normalized = normalizeDecimalInput(value)
  return new Money(normalized || '0', currency).toAmountString()
}

function parseDecimal(value: string) {
  const parsed = Number(normalizeDecimalInput(value))
  return Number.isFinite(parsed) ? parsed : 0
}

function isPositiveAmount(value: string) {
  return parseDecimal(value) > 0
}

const minimumProductBasePriceAmount = '10.000'

function productMinimumPriceMessage(currency: Currency) {
  return `O valor mínimo deste produto é ${formatBuyerPrice(money(minimumProductBasePriceAmount, currency))}.`
}

function isAtLeastProductMinimum(value: Product['price']) {
  return !Money.fromMonetaryValue(value)
    .subtract(new Money(minimumProductBasePriceAmount, value.currency))
    .isNegative()
}

function priceRequiredMessage() {
  return 'Informe um preço maior que zero.'
}

function trialPriceMinimumMessage(currency: Currency) {
  return `A entrada paga precisa ser de pelo menos ${formatBuyerPrice(money(minimumProductBasePriceAmount, currency))}. Para não cobrar nada no teste, use Teste grátis.`
}

function validateProductPricing(product: Product): ProductEditorFieldErrors {
  const errors: ProductEditorFieldErrors = {}
  const primaryOfferId = primaryOfferIdFor(product)

  if (!isAtLeastProductMinimum(product.price)) {
    errors['price.amount'] = productMinimumPriceMessage(product.price.currency)
  }

  product.offers.forEach((offer, index) => {
    if (isAtLeastProductMinimum(offer.price)) return
    const message = productMinimumPriceMessage(offer.price.currency)
    const isPrimary = offer.id === primaryOfferId || (primaryOfferId.length === 0 && index === 0)

    errors[`offers.${index}.price.amount`] = message
    errors[`offers.${offer.id}.price.amount`] = message
    if (isPrimary) {
      errors['price.amount'] = message
    }
  })

  return errors
}

function offerRequiredMessage() {
  return 'Selecione a oferta.'
}

function couponCodeRequiredMessage() {
  return 'Informe o código do cupom.'
}

function emailInvalidMessage() {
  return 'Informe um e-mail válido.'
}

function planRequiredMessage() {
  return 'Crie pelo menos um plano de assinatura.'
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

function validateProduct(product: Product, vendepayFee?: MonetaryValue): ProductEditorFieldErrors {
  const errors: ProductEditorFieldErrors = {
    ...validateProductPricing(product),
    ...validateProductPixels(product.features.tracking ? product.pixels : []),
  }
  const productNameError = validateProductName(product.name)
  if (productNameError) errors.name = productNameError
  const statementDescriptorError = validateStatementDescriptor(product.statementDescriptor)

  if (statementDescriptorError) {
    errors.statementDescriptor = statementDescriptorError
  }

  if (product.priceModel === 'subscription') {
    if (product.recurrencePlans.length === 0) {
      errors['recurrencePlans'] = planRequiredMessage()
    }
    product.recurrencePlans.forEach((plan, index) => {
      if (!isAtLeastProductMinimum(plan.value)) {
        const message = productMinimumPriceMessage(plan.value.currency)
        errors[`recurrencePlans.${plan.id}.value.amount`] = message
        errors[`recurrencePlans.${index}.value.amount`] = message
      }
      if (
        plan.trialEnabled &&
        plan.trialType === 'PAID' &&
        !isAtLeastProductMinimum(plan.trialPrice)
      ) {
        const message = trialPriceMinimumMessage(plan.value.currency)
        errors[`recurrencePlans.${plan.id}.trialPrice.amount`] = message
        errors[`recurrencePlans.${index}.trialPrice.amount`] = message
      }
    })
  }

  if (product.features.funnel) {
    product.orderBumps.forEach((bump, index) => {
      if (!isAtLeastProductMinimum(bump.price)) {
        const message = productMinimumPriceMessage(bump.price.currency)
        errors[`orderBumps.${bump.id}.price.amount`] = message
        errors[`orderBumps.${index}.price.amount`] = message
      }
      if (!bump.targetOfferId) {
        errors[`orderBumps.${bump.id}.targetOfferId`] = offerRequiredMessage()
      }
    })
    product.funnelNodes.forEach((node, index) => {
      if (!isAtLeastProductMinimum(node.price)) {
        const message = productMinimumPriceMessage(node.price.currency)
        errors[`funnelNodes.${node.id}.price.amount`] = message
        errors[`funnelNodes.${index}.price.amount`] = message
      }
      if (!node.offerId) {
        errors[`funnelNodes.${node.id}.offerId`] = offerRequiredMessage()
      }
    })
  }

  if (product.features.coupons) {
    product.coupons.forEach((coupon) => {
      if (!sanitizeCouponCode(coupon.code)) {
        errors[`coupons.${coupon.id}.code`] = couponCodeRequiredMessage()
      }
      if (coupon.type === 'fixed' && !isPositiveAmount(coupon.amount.amount)) {
        errors[`coupons.${coupon.id}.amount.amount`] = priceRequiredMessage()
      }
      const endDateError = couponEndDateError(coupon, isoDateFromLocal(new Date()))
      if (endDateError) {
        errors[`coupons.${coupon.id}.endsAt`] = endDateError
      }
    })
  }

  if (product.features.coproduction) {
    product.coproducers.forEach((coproducer) => {
      if (coproducer.email.trim() && !isValidEmail(coproducer.email)) {
        errors[`coproducers.${coproducer.id}.email`] = emailInvalidMessage()
      }
    })
  }

  if (!/^#[0-9a-fA-F]{6}$/.test(product.emailTemplate.brandColor)) {
    errors['emailTemplate.brandColor'] = 'Informe uma cor hexadecimal válida.'
  } else if (!hasReadableEmailButtonContrast(product.emailTemplate.brandColor)) {
    errors['emailTemplate.brandColor'] = 'Use uma cor com mais contraste para texto branco.'
  }

  if (
    product.emailTemplate.logoUrl.trim().length > 0 &&
    !/^https?:\/\//i.test(product.emailTemplate.logoUrl.trim())
  ) {
    errors['emailTemplate.logoUrl'] = 'Informe uma URL absoluta começando com http ou https.'
  }

  if (vendepayFee?.currency === product.price.currency) {
    const splitPreview = computeSplitPreview({
      amount: product.price,
      vendepayFee,
      affiliatePercentage: product.affiliate.enabled ? product.affiliate.commission : '0',
      coProducerPercentage: product.coproducers[0]?.percentage ?? '0',
    })
    if (splitPreview.isInvalid) {
      errors['split'] = 'O split deixa um participante negativo.'
    }
  }

  return errors
}

function focusFirstFieldError(errors: ProductEditorFieldErrors, step: ProductStepId) {
  const firstPath = Object.keys(errors).find((path) => stepForFieldPath(path) === step)
  if (!firstPath) return
  requestAnimationFrame(() => {
    const element = document.getElementById(`field-${firstPath}`)
    if (!element) return
    element.scrollIntoView({ block: 'center', behavior: 'smooth' })
    element.focus()
  })
}

function pricingErrorsFromApi(product: Product, error: unknown): ProductEditorFieldErrors {
  const errors: ProductEditorFieldErrors = {}

  for (const fieldError of extractApiFieldErrors(error)) {
    if (fieldError.path === 'price.amount') {
      errors['price.amount'] = productMinimumPriceMessage(product.price.currency)
      continue
    }

    if (fieldError.path.includes('trialPrice')) {
      const planMatch = /^recurrencePlans\.(\d+)\./.exec(fieldError.path)
      const planIndex = planMatch ? Number(planMatch[1]) : undefined
      addTrialPriceApiError(product, errors, planIndex)
      continue
    }

    const offerMatch = /^offers\.(\d+)\.price\.amount$/.exec(fieldError.path)
    if (!offerMatch) continue

    const offerIndex = Number(offerMatch[1])
    const offer = product.offers[offerIndex]
    const message = productMinimumPriceMessage(offer?.price.currency ?? product.price.currency)
    if (!offer) continue
    const primaryOfferId = primaryOfferIdFor(product)
    const isPrimary =
      offer.id === primaryOfferId || (primaryOfferId.length === 0 && offerIndex === 0)
    errors[fieldError.path] = message
    errors[`offers.${offer.id}.price.amount`] = message
    if (isPrimary) {
      errors['price.amount'] = message
    }
  }

  if (
    Object.keys(errors).length === 0 &&
    error instanceof Error &&
    error.message.includes('trialPrice')
  ) {
    addTrialPriceApiError(product, errors)
  }

  return errors
}

function addTrialPriceApiError(
  product: Product,
  errors: ProductEditorFieldErrors,
  planIndex?: number,
) {
  const plans =
    planIndex === undefined
      ? product.recurrencePlans
      : [product.recurrencePlans[planIndex]].filter(Boolean)
  for (const plan of plans) {
    if (!plan || !plan.trialEnabled || plan.trialType !== 'PAID') continue
    const index = product.recurrencePlans.findIndex((candidate) => candidate.id === plan.id)
    const message = trialPriceMinimumMessage(plan.value.currency)
    errors[`recurrencePlans.${plan.id}.trialPrice.amount`] = message
    if (index >= 0) {
      errors[`recurrencePlans.${index}.trialPrice.amount`] = message
    }
  }
}

function clampNumber(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, value))
}

function clampIntegerInput(value: string, min: number, max: number) {
  return Math.round(clampNumber(Number(value), min, max))
}

function cleanPercentage(value: string, min: number, max: number) {
  return percentageInputToFixed(value, min, max)
}

function sanitizeStatementDescriptor(value: string) {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, '')
    .trim()
    .slice(0, 22)
}

function normalizeProductForSave(product: Product): Product {
  const normalizedPaymentMethods = availablePaymentMethods(product.paymentMethods)

  return {
    ...product,
    paymentMethods:
      normalizedPaymentMethods.length > 0 ? normalizedPaymentMethods : ['CREDIT_CARD'],
    price: money(
      toLedgerAmount(product.price.amount, product.price.currency),
      product.price.currency,
    ),
    maxInstallments: 1,
    refundDays: clampIntegerInput(String(product.refundDays), 0, 365),
    statementDescriptor: sanitizeStatementDescriptor(product.statementDescriptor),
    offers: product.offers.map((offer) => ({
      ...offer,
      name:
        MAX_OFFERS_PER_PRODUCT > 1 || offer.id !== primaryOfferIdOf(product)
          ? offer.name
          : product.name.trim() || offer.name,
      price: money(toLedgerAmount(offer.price.amount, offer.price.currency), offer.price.currency),
    })),
    orderBumps: product.orderBumps.map((bump) => ({
      ...bump,
      price: money(toLedgerAmount(bump.price.amount, bump.price.currency), bump.price.currency),
    })),
    funnelNodes: product.funnelNodes.map((node) => ({
      ...node,
      price: money(toLedgerAmount(node.price.amount, node.price.currency), node.price.currency),
    })),
    affiliate: {
      ...product.affiliate,
      commission: cleanPercentage(product.affiliate.commission, 5, 90),
    },
    coupons: product.coupons.map((coupon, index) => {
      const { startsAt, endsAt } = couponStorageWindow(coupon)
      const code = sanitizeCouponCode(coupon.code) || `CUPOM${index + 1}`
      const couponCurrency = product.price.currency
      const couponAmount = couponAmountForCurrency(coupon, couponCurrency)
      const amount = toLedgerAmount(
        coupon.type === 'fixed' && isPositiveAmount(coupon.amount.amount)
          ? coupon.amount.amount
          : '1.000',
        couponAmount.currency,
      )

      return {
        ...coupon,
        code,
        percent: coupon.type === 'percent' ? clampNumber(coupon.percent, 1, 95) : 0,
        amount: money(amount, couponAmount.currency),
        startsAt,
        endsAt,
        usageLimit: clampIntegerInput(String(coupon.usageLimit), 1, 999999),
      }
    }),
    coproducers: product.coproducers.map((coproducer) => ({
      ...coproducer,
      percentage: cleanPercentage(coproducer.percentage, 1, 99),
    })),
    recurrencePlans: product.recurrencePlans.map((plan) => {
      const trialDays = plan.trialEnabled ? clampIntegerInput(String(plan.trialDays), 1, 365) : 0
      const graceDays = plan.graceEnabled ? clampIntegerInput(String(plan.graceDays), 1, 60) : 0

      return {
        ...plan,
        value: money(toLedgerAmount(plan.value.amount, plan.value.currency), plan.value.currency),
        billingPeriodCount: clampIntegerInput(String(plan.billingPeriodCount), 1, 365),
        billingAnchorType:
          plan.billingPeriodUnit === 'MONTH' ? plan.billingAnchorType : 'IMMEDIATE',
        billingAnchorDayOfMonth:
          plan.billingPeriodUnit === 'MONTH' && plan.billingAnchorType === 'CALENDAR'
            ? clampIntegerInput(String(plan.billingAnchorDayOfMonth ?? 1), 1, 30)
            : null,
        trialEnabled: trialDays > 0,
        trialDays,
        trialPrice: money(
          toLedgerAmount(plan.trialPrice.amount, plan.value.currency),
          plan.value.currency,
        ),
        retryAttempts: clampIntegerInput(String(plan.retryAttempts), 0, 10),
        retryDelayDays: clampIntegerInput(String(plan.retryDelayDays), 1, 30),
        graceDays,
      }
    }),
  }
}

async function saveRecurrencePlan(productId: string, plan: RecurrencePlan, creating: boolean) {
  const endpoint = creating ? `/admin/products/${productId}/plans` : `/admin/plans/${plan.id}`
  const data = await apiFetch<unknown>(endpoint, {
    method: 'POST',
    body: recurrencePlanRequestBody(plan),
  })
  return RecurrencePlanSchema.parse(data)
}

async function syncRecurrencePlans(
  productId: string,
  nextPlans: RecurrencePlan[],
  previousPlans: RecurrencePlan[],
) {
  const previousPlanIds = new Set(previousPlans.map((plan) => plan.id))
  const savedPlans = await Promise.all(
    nextPlans.map((plan) => saveRecurrencePlan(productId, plan, !previousPlanIds.has(plan.id))),
  )
  const removedPersistedPlans = plansToDeactivate(nextPlans, previousPlans)

  if (removedPersistedPlans.length > 0) {
    await Promise.all(
      removedPersistedPlans.map((plan) =>
        saveRecurrencePlan(
          productId,
          {
            ...plan,
            active: false,
          },
          false,
        ),
      ),
    )
  }

  return savedPlans
}

type DraftProductState = {
  productId: string
  sourceKey: string
  draft: Product
  saveState: FocusSaveState
}

type ActiveStepState = {
  productId: string | undefined
  step: ProductStepId
}

const productTypeFeatureDefaults: Record<ProductType, Product['features']> = {
  course: {
    funnel: true,
    tracking: false,
    affiliates: false,
    global: true,
    coupons: false,
    coproduction: false,
    email: false,
    recurrence: false,
  },
  ebook: {
    funnel: false,
    tracking: false,
    affiliates: false,
    global: true,
    coupons: false,
    coproduction: false,
    email: false,
    recurrence: false,
  },
  service: {
    funnel: false,
    tracking: false,
    affiliates: false,
    global: true,
    coupons: false,
    coproduction: false,
    email: false,
    recurrence: false,
  },
}

const productTypeDeliveryDefaults: Record<ProductType, Product['deliveryType']> = {
  course: 'members_area',
  ebook: 'digital',
  service: 'external_url',
}

function applyProductType(product: Product, productType: ProductType): Product {
  return {
    ...product,
    productType,
    features: productTypeFeatureDefaults[productType],
    deliveryType: productTypeDeliveryDefaults[productType],
  }
}

function getVisibleStepItems(
  features: Product['features'],
  activeStep: ProductStepId,
  disabled = false,
  product?: Product,
  fieldErrors: ProductEditorFieldErrors = {},
): StepTrailItem[] {
  const visibleStepIds = allProductSteps
    .filter((step) => step.feature === undefined || features[step.feature])
    .map((step) => step.id)
  const activeVisibleIndex = visibleStepIds.indexOf(activeStep)
  let visibleIndex = -1

  return allProductSteps.map((step) => {
    const visible = step.feature === undefined || features[step.feature]
    if (visible) {
      visibleIndex += 1
    }
    const baseTag = product ? getStepTag(product, step.id) : undefined
    const tagLabel = stepHasFieldError(step.id, fieldErrors) ? 'Incompleto' : baseTag
    const visibleTagLabel = tagLabel && tagLabel !== 'Pendente' ? tagLabel : undefined

    return {
      id: step.id,
      title: step.title,
      icon: stepIcons[step.id],
      visible,
      disabled: disabled && step.id !== activeStep,
      tagLabel: visibleTagLabel,
      status: product
        ? getStepStatus(tagLabel, activeStep === step.id)
        : activeStep === step.id
          ? 'current'
          : activeVisibleIndex >= 0 && visibleIndex < activeVisibleIndex
            ? 'complete'
            : 'pending',
    }
  })
}

function getProductSourceKey(product: Product) {
  const coproductionKey = product.coproducers
    .map((coproducer) =>
      [coproducer.id, coproducer.email, coproducer.percentage, coproducer.status].join(':'),
    )
    .join('|')
  return `${product.id}:${product.updatedAt}:${coproductionKey}`
}

function ProductValidationSummary({ errors }: { errors: ProductEditorFieldErrors }) {
  const count = Object.keys(errors).length
  if (count === 0) return null

  return (
    <Banner tone="danger" title="Revise o produto">
      {count === 1
        ? 'Corrija o campo destacado antes de salvar.'
        : `Corrija os ${count} campos destacados antes de salvar.`}
    </Banner>
  )
}

function useDraftProduct(product: Product | undefined) {
  const [localByProductId, setLocalByProductId] = useState<Record<string, DraftProductState>>({})

  if (!product) {
    return {
      draft: null,
      patch: () => undefined,
      saveState: 'saved' as FocusSaveState,
      setSaveState: () => undefined,
      discardDraft: () => undefined,
    }
  }

  const sourceProduct = product
  const sourceKey = getProductSourceKey(sourceProduct)
  const local = localByProductId[sourceProduct.id]
  const localMatchesProduct = local?.productId === sourceProduct.id
  const shouldUseLocal =
    local && localMatchesProduct && (local.saveState !== 'saved' || local.sourceKey === sourceKey)
  const draft = shouldUseLocal ? local.draft : sourceProduct
  const saveState = shouldUseLocal ? local.saveState : 'saved'

  function patch(updater: (current: Product) => Product) {
    setLocalByProductId((current) => {
      const currentLocal = current[sourceProduct.id]
      const base =
        currentLocal && (currentLocal.saveState !== 'saved' || currentLocal.sourceKey === sourceKey)
          ? currentLocal.draft
          : sourceProduct

      return {
        ...current,
        [sourceProduct.id]: {
          productId: sourceProduct.id,
          sourceKey,
          draft: updater(base),
          saveState: 'dirty',
        },
      }
    })
  }

  function setSaveState(nextSaveState: FocusSaveState) {
    setLocalByProductId((current) => {
      const currentLocal = current[sourceProduct.id]

      return {
        ...current,
        [sourceProduct.id]: {
          productId: sourceProduct.id,
          sourceKey,
          draft:
            currentLocal &&
            (currentLocal.saveState !== 'saved' || currentLocal.sourceKey === sourceKey)
              ? currentLocal.draft
              : sourceProduct,
          saveState: nextSaveState,
        },
      }
    })
  }

  function discardDraft(productIdToDiscard = sourceProduct.id) {
    setLocalByProductId((current) => {
      if (current[productIdToDiscard] === undefined) return current
      const next = { ...current }
      delete next[productIdToDiscard]
      return next
    })
  }

  return { draft, patch, saveState, setSaveState, discardDraft }
}

function getRailSaveLabel(state: FocusSaveState) {
  if (state === 'saving') return 'Salvando alterações'
  if (state === 'dirty') return 'Alterações locais'
  return 'Salvo automaticamente'
}

function ProductEditorRail({
  children,
  saveState,
}: {
  children: ReactNode
  saveState: FocusSaveState
}) {
  const Icon = saveState === 'saving' ? CloudCog : CloudCheck

  return (
    <div className={editorRail}>
      <span className={editorRailTitle}>Etapas</span>
      {children}
      <div className={editorRailSave}>
        <Icon size={15} />
        <span>{getRailSaveLabel(saveState)}</span>
      </div>
    </div>
  )
}

export function ProductEditor({
  open,
  productId,
  adminProducerId,
  minuteBillingEnabled = false,
  approvalContext,
  onClose,
  onAfterClose,
  onOpenCheckoutBuilder,
}: ProductEditorProps) {
  const queryClient = useQueryClient()
  const [activeStepState, setActiveStepState] = useState<ActiveStepState>({
    productId,
    step: 'basico',
  })
  const [fieldErrors, setFieldErrors] = useState<ProductEditorFieldErrors>({})
  const productResult = useQuery<Product>({
    queryKey: productKeys.adminDetail(productId ?? ''),
    queryFn: async () => {
      const data = await apiFetch<unknown>(`/admin/products/${productId}`)
      return parseProductResponse(data)
    },
    enabled: Boolean(productId),
  })
  const productListProducerId = adminProducerId ?? productResult.data?.producerId ?? ''
  const productsResult = useQuery<Product[]>({
    queryKey: productKeys.adminList(productListProducerId),
    queryFn: async () => {
      const data = await apiFetch<unknown>(`/admin/users/${productListProducerId}/products`)
      return parseProductsResponse(data)
    },
    enabled: Boolean(productListProducerId),
  })
  const coproductionResult = useQuery<CoproductionInvite | null>({
    queryKey: coproductionKeys.adminForProduct(productId ?? ''),
    queryFn: () => getAdminCoproductionForProduct(productId ?? ''),
    enabled: Boolean(productId),
  })
  const productFromList = productsResult.data?.find((product) => product.id === productId)
  const productFromDetail = productResult.data?.id === productId ? productResult.data : undefined
  const coproductionInvite = coproductionResult.data
  const editorProduct = useMemo(() => {
    const base = productFromDetail ?? productFromList
    if (!base) return base
    const primaryPricing = projectPrimaryPricing(base)
    const coproducers =
      coproductionInvite && coproductionInvite.status !== 'REMOVIDO'
        ? [inviteToCoproducer(coproductionInvite)]
        : []
    return { ...primaryPricing, coproducers }
  }, [productFromDetail, productFromList, coproductionInvite])
  const updateProductMutation = useUpdateProductMutation(productId ?? '')
  const activateProductMutation = useActivateProductMutation(productId ?? '')
  const archiveProductMutation = useArchiveProductMutation(productId ?? '')
  const { draft, patch, saveState, setSaveState, discardDraft } = useDraftProduct(editorProduct)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const feePreviewProducerId = draft?.producerId || productListProducerId
  const feePreviewAmount = draft?.price.amount ?? '0'
  const debouncedFeePreviewAmount = useDebounce(feePreviewAmount, 300)
  const feePreviewInputIsCurrent = debouncedFeePreviewAmount === feePreviewAmount
  const feePreviewEnabled = Boolean(
    draft && feePreviewProducerId && isPositiveAmount(feePreviewAmount),
  )
  const feePreviewResult = useQuery(
    adminFeePreviewQuery(
      {
        producerId: feePreviewProducerId,
        amount: debouncedFeePreviewAmount,
        currency: draft?.price.currency ?? 'BRL',
      },
      Boolean(draft && feePreviewProducerId && isPositiveAmount(debouncedFeePreviewAmount)),
    ),
  )
  const currentFeePreview =
    draft && feePreviewInputIsCurrent
      ? usableFeePreview(feePreviewResult.data, draft.price, feePreviewResult.isError)
      : undefined
  const feePreviewState: FeePreviewState = {
    data: currentFeePreview,
    isEnabled: feePreviewEnabled,
    isLoading:
      feePreviewEnabled &&
      (!feePreviewInputIsCurrent || (!feePreviewResult.isError && currentFeePreview === undefined)),
    isError: feePreviewEnabled && feePreviewInputIsCurrent && feePreviewResult.isError,
    onRetry: () => void feePreviewResult.refetch(),
  }

  let visibleFieldErrors = fieldErrors
  if (currentFeePreview && fieldErrors.split === feePreviewValidationError) {
    visibleFieldErrors = { ...fieldErrors }
    delete visibleFieldErrors.split
  }
  const activeStep = activeStepState.productId === productId ? activeStepState.step : 'basico'
  const apiContextValue = useMemo<ProductEditorApiContextValue>(
    () => ({
      ...(productListProducerId ? { adminProducerId: productListProducerId } : {}),
    }),
    [productListProducerId],
  )

  useEffect(() => {
    if (!productId || !productFromList) return
    queryClient.setQueryData(productKeys.adminDetail(productId), (current: Product | undefined) =>
      current?.id === productId ? current : productFromList,
    )
  }, [productFromList, productId, queryClient])

  function setActiveStep(next: ProductStepId | ((current: ProductStepId) => ProductStepId)) {
    const step = typeof next === 'function' ? next(activeStep) : next
    setActiveStepState({ productId, step })
  }

  const steps: StepTrailItem[] = draft
    ? getVisibleStepItems(draft.features, activeStep, false, draft, visibleFieldErrors)
    : []
  const visibleSteps = steps.filter((step) => step.visible !== false)
  const activeStepIndex = visibleSteps.findIndex((step) => step.id === activeStep)
  const nextStep = activeStepIndex >= 0 ? visibleSteps[activeStepIndex + 1] : undefined

  function clearFieldErrors(...paths: string[]) {
    setFieldErrors((current) => {
      if (paths.every((path) => current[path] === undefined)) return current
      const next = { ...current }
      for (const path of paths) {
        delete next[path]
      }
      return next
    })
  }

  async function saveDraft(product = draft) {
    if (!product || !productId) return false
    if (isSavingDraft) return false
    setIsSavingDraft(true)
    try {
      return await saveDraftUnlocked(product)
    } finally {
      setIsSavingDraft(false)
    }
  }

  async function saveDraftUnlocked(product: Product) {
    const productWithCurrentPlanPrices = synchronizeFunnelPlanPrices(product, productsResult.data)
    const previewForSave =
      feePreviewProducerId && isPositiveAmount(productWithCurrentPlanPrices.price.amount)
        ? await resolveFeePreviewForSave(
            feePreviewResult.data,
            productWithCurrentPlanPrices.price,
            feePreviewResult.isError,
            () =>
              queryClient.fetchQuery(
                adminFeePreviewQuery({
                  producerId: feePreviewProducerId,
                  amount: productWithCurrentPlanPrices.price.amount,
                  currency: productWithCurrentPlanPrices.price.currency,
                }),
              ),
          )
        : undefined
    const validationErrors = validateProduct(productWithCurrentPlanPrices, previewForSave?.totalFee)
    if (
      isPositiveAmount(productWithCurrentPlanPrices.price.amount) &&
      previewForSave === undefined
    ) {
      validationErrors['split'] = feePreviewValidationError
    }
    if (Object.keys(validationErrors).length > 0) {
      if (productWithCurrentPlanPrices !== product) {
        patch(() => productWithCurrentPlanPrices)
      }
      setFieldErrors(validationErrors)
      const firstStep = firstStepWithError(validationErrors)
      setActiveStep(firstStep)
      toast.error({
        title: 'Revise o produto',
        description: 'Preencha os campos destacados antes de salvar.',
      })
      focusFirstFieldError(validationErrors, firstStep)
      return false
    }

    try {
      const normalizedProduct = normalizeProductForSave(productWithCurrentPlanPrices)
      setSaveState('saving')
      const savedProduct = await updateProductMutation.mutateAsync(normalizedProduct)
      let finalProduct = savedProduct

      if (normalizedProduct.priceModel === 'subscription') {
        const recurrencePlans = await syncRecurrencePlans(
          savedProduct.id,
          normalizedProduct.recurrencePlans,
          productFromDetail?.recurrencePlans ?? productFromList?.recurrencePlans ?? [],
        )
        finalProduct = ProductSchema.parse({
          ...savedProduct,
          recurrencePlans,
        })
        queryClient.setQueryData(productKeys.adminDetail(finalProduct.id), finalProduct)
        queryClient.setQueryData<Product[]>(
          productKeys.adminList(finalProduct.producerId),
          (current) => current?.map((item) => (item.id === finalProduct.id ? finalProduct : item)),
        )
      }

      try {
        const coproductionSync = await syncCoproduction(
          savedProduct.id,
          normalizedProduct.coproducers[0],
          editorProduct?.coproducers[0],
        )
        if (coproductionSync.changed) {
          const coproductionQueryKey = coproductionKeys.adminForProduct(savedProduct.id)
          queryClient.setQueryData(coproductionQueryKey, coproductionSync.invite)
          const coproducers =
            coproductionSync.invite && coproductionSync.invite.status !== 'REMOVIDO'
              ? [inviteToCoproducer(coproductionSync.invite)]
              : []
          finalProduct = ProductSchema.parse({ ...finalProduct, coproducers })
          queryClient.setQueryData(productKeys.adminDetail(finalProduct.id), finalProduct)
        }
      } catch (coproductionError) {
        await queryClient.invalidateQueries({
          queryKey: coproductionKeys.adminForProduct(savedProduct.id),
        })
        throw coproductionError
      }

      setFieldErrors({})
      setSaveState('saved')
      toast.success('Produto salvo.')
      return true
    } catch (error) {
      const pricingFieldErrors = pricingErrorsFromApi(product, error)
      if (Object.keys(pricingFieldErrors).length > 0) {
        setFieldErrors(pricingFieldErrors)
        setActiveStep('preco')
        toast.error({
          title: 'Revise o preço',
          description: 'Confira os campos destacados antes de salvar.',
        })
        setSaveState('dirty')
        return false
      }

      setSaveState('dirty')
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível salvar o produto',
          fallback: 'Suas alterações continuam nesta tela. Corrija o problema e tente novamente.',
        }),
      )
      return false
    }
  }

  async function publish() {
    if (!draft || !productId) return
    if (draft.status === 'ACTIVE') return
    const saved = await saveDraft(draft)
    if (!saved) return
    try {
      await activateProductMutation.mutateAsync()
      toast.success('Produto publicado.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível publicar',
          fallback: 'Revise as pendências do produto e tente novamente.',
        }),
      )
    }
  }

  async function unpublish() {
    if (!draft || !productId || draft.status !== 'ACTIVE') return
    try {
      const product = await archiveProductMutation.mutateAsync()
      discardDraft(product.id)
      toast.success('Produto despublicado.')
    } catch (error) {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível despublicar',
          fallback: 'Tente novamente em alguns instantes.',
        }),
      )
    }
  }

  function setPublished(next: boolean) {
    if (next) {
      void publish()
    } else {
      void unpublish()
    }
  }

  function setFeature(feature: ProductFeature, enabled: boolean) {
    if (!draft) return
    const nextFeatures = { ...draft.features, [feature]: enabled }
    patch((current) => ({ ...current, features: nextFeatures }))
    setActiveStep((current) => fallbackStepForHidden(current, nextFeatures))
  }

  async function saveAndGoNext() {
    if (!nextStep) return
    const saved = saveState === 'dirty' ? await saveDraft() : true
    if (saved) {
      setActiveStep(nextStep.id as ProductStepId)
    }
  }

  if ((productResult.isPending && !editorProduct) || !draft) {
    return (
      <ProductEditorApiContext.Provider value={apiContextValue}>
        <FocusEditor
          open={open}
          onClose={onClose}
          onAfterClose={onAfterClose}
          breadcrumb="Produtos › Carregando"
          title="Carregando produto"
          rail={
            <ProductEditorRail saveState="saved">
              <StepTrail steps={[]} currentStep={1} orientation="vertical" />
            </ProductEditorRail>
          }
        >
          <Card>Carregando...</Card>
        </FocusEditor>
      </ProductEditorApiContext.Provider>
    )
  }

  const status = getProductStatusPresentation(draft.status)
  const StatusIcon = status.icon

  return (
    <ProductEditorApiContext.Provider value={apiContextValue}>
      <FocusEditor
        open={open}
        onClose={onClose}
        onAfterClose={onAfterClose}
        breadcrumb={`Produtos › Editando › ${draft.name}`}
        title={draft.name}
        rail={
          <ProductEditorRail saveState={saveState}>
            <StepTrail
              steps={steps}
              activeId={activeStep}
              orientation="vertical"
              onStepClick={(id) => setActiveStep(id as ProductStepId)}
            />
          </ProductEditorRail>
        }
        saveState={saveState}
      >
        <div className={editorStack}>
          {approvalContext ? (
            <Banner tone="success" title="Alteração autorizada nesta sessão">
              Aprovação informada por <strong>{approvalContext.approvedBy}</strong> · referência{' '}
              <strong>{approvalContext.reference}</strong> · registrada em{' '}
              <strong>{formatApprovalDate(approvalContext.approvedAt)}</strong>. As alterações
              salvas continuam registradas pela auditoria administrativa.
            </Banner>
          ) : null}
          <div className={sectionHeader}>
            <StatusBadge tone={status.tone} label={status.label} icon={<StatusIcon size={13} />} />
            <div className={actionRow}>
              <Button
                variant="secondary"
                leftIcon={<Save size={16} />}
                onClick={() => void saveDraft()}
                disabled={isSavingDraft || updateProductMutation.isPending}
              >
                Salvar seção
              </Button>
              {nextStep ? (
                <Button
                  rightIcon={<ArrowRight size={16} />}
                  onClick={() => void saveAndGoNext()}
                  disabled={isSavingDraft || updateProductMutation.isPending}
                >
                  Salvar e continuar
                </Button>
              ) : null}
            </div>
          </div>
          <ProductValidationSummary errors={visibleFieldErrors} />
          {activeStep === 'basico'
            ? renderBasic(draft, patch, visibleFieldErrors, clearFieldErrors)
            : null}
          {activeStep === 'recursos' ? renderFeatures(draft, setFeature) : null}
          {activeStep === 'preco'
            ? renderPricing(
                draft,
                patch,
                visibleFieldErrors,
                clearFieldErrors,
                feePreviewState,
                minuteBillingEnabled,
              )
            : null}
          {activeStep === 'funnel'
            ? renderFunnel(draft, patch, visibleFieldErrors, clearFieldErrors)
            : null}
          {activeStep === 'tracking'
            ? renderTracking(draft, patch, visibleFieldErrors, clearFieldErrors)
            : null}
          {activeStep === 'afiliados' ? renderAffiliates(draft, patch) : null}
          {activeStep === 'cupons'
            ? renderCoupons(draft, patch, visibleFieldErrors, clearFieldErrors)
            : null}
          {activeStep === 'coproducao'
            ? renderCoproduction(
                draft,
                patch,
                visibleFieldErrors,
                clearFieldErrors,
                coproductionInvite,
                feePreviewState,
              )
            : null}
          {activeStep === 'email' ? (
            <EmailStep
              product={draft}
              patch={patch}
              fieldErrors={visibleFieldErrors}
              clearFieldErrors={clearFieldErrors}
            />
          ) : null}
          {activeStep === 'links'
            ? renderLinks(
                draft,
                {
                  onSetPublished: setPublished,
                  isPending: activateProductMutation.isPending || archiveProductMutation.isPending,
                },
                onOpenCheckoutBuilder,
              )
            : null}
        </div>
      </FocusEditor>
    </ProductEditorApiContext.Provider>
  )
}

function formatApprovalDate(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed)
}

function ProductTypeChoices({
  value,
  onChange,
}: {
  value: ProductType
  onChange: (value: ProductType) => void
}) {
  return (
    <div className={quickstartChoiceGroup}>
      <span className={quickstartGroupLabel}>Tipo de produto</span>
      <div className={productTypeGrid} role="radiogroup" aria-label="Tipo de produto">
        {quickProductTypeOptions.map((option) => {
          const active = option.value === value
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              className={productTypeCard({ active })}
              onClick={() => onChange(option.value)}
            >
              <span className={productTypeIcon}>{option.icon}</span>
              <span className={productTypeTitle}>{option.label}</span>
              <span className={productTypeCopy}>{option.description}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function CategoryChoices({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className={quickstartChoiceGroup}>
      <span className={quickstartGroupLabel}>Categoria</span>
      <div className={productTypeGrid} role="radiogroup" aria-label="Categoria">
        {categoryOptions.map((option) => {
          const active = productCategoriesMatch(option.value, value)
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              className={productTypeCard({ active, density: 'compact' })}
              onClick={() => onChange(option.value)}
            >
              <span className={productTypeIcon}>{option.icon}</span>
              <span className={productTypeTitle}>{option.label}</span>
              <span className={productTypeCopy}>{option.description}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function renderBasic(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
) {
  return (
    <Card className={section}>
      <div>
        <h2 className={sectionTitle}>Básico</h2>
        <p className={sectionCopy}>Dados essenciais, suporte e apresentação do produto.</p>
      </div>
      <ProductTypeChoices
        value={product.productType}
        onChange={(productType) => patch((current) => applyProductType(current, productType))}
      />
      <CategoryChoices
        value={product.category}
        onChange={(category) => patch((current) => ({ ...current, category }))}
      />
      <div className={basicDetailsGrid}>
        <div className={basicMainStack}>
          <TextField
            id="field-name"
            label="Nome"
            required
            value={product.name}
            error={fieldErrors.name}
            onChange={(event) => {
              clearFieldErrors('name')
              patch((current) => ({ ...current, name: event.target.value }))
            }}
          />
          <TextField
            label="Descrição"
            value={product.description}
            onChange={(event) =>
              patch((current) => ({ ...current, description: event.target.value }))
            }
          />
          <div className={basicSupportGrid}>
            <TextField
              label="E-mail de suporte"
              value={product.supportEmail}
              onChange={(event) =>
                patch((current) => ({ ...current, supportEmail: event.target.value }))
              }
            />
            <TextField
              label="Nome do suporte"
              value={product.supportName}
              onChange={(event) =>
                patch((current) => ({ ...current, supportName: event.target.value }))
              }
            />
          </div>
          <div className={basicSupportGrid}>
            <TextField
              label="Garantia (dias)"
              type="number"
              min={0}
              value={product.refundDays}
              onChange={(event) =>
                patch((current) => ({
                  ...current,
                  refundDays: clampIntegerInput(event.target.value, 0, 365),
                }))
              }
            />
            <DeliveryField product={product} patch={patch} />
          </div>
        </div>
        <div className={basicAsideStack}>
          <ImageUploadField
            label="Capa do produto"
            kind="cover"
            value={product.coverImage}
            onChange={(coverImage) => patch((current) => ({ ...current, coverImage }))}
            aspect="1 / 1"
            maxInlineSize={320}
            hint="ideal 1200x1200"
            allowUrl
          />
        </div>
      </div>
    </Card>
  )
}

function renderFeatures(
  product: Product,
  setFeature: (feature: ProductFeature, enabled: boolean) => void,
) {
  return (
    <Card className={section}>
      <div>
        <h2 className={sectionTitle}>Recursos</h2>
        <p className={sectionCopy}>
          Ative o que este produto precisa. Desativar oculta a etapa sem apagar dados.
        </p>
      </div>
      <div className={featureGrid}>
        {featureDefinitions.map((feature) => (
          <div key={feature.key} className={featureCard}>
            <div>
              <div className={inlineRow}>
                <span className={featureTitle}>{feature.title}</span>
                {feature.badge ? <span className={badge}>{feature.badge}</span> : null}
              </div>
              <p className={muted}>{feature.description}</p>
            </div>
            <Toggle
              checked={product.features[feature.key]}
              onChange={(checked) => setFeature(feature.key, checked)}
              ariaLabel={feature.title}
            />
          </div>
        ))}
      </div>
    </Card>
  )
}

function renderPricing(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
  feePreviewState: FeePreviewState,
  minuteBillingEnabled: boolean,
) {
  const hasMinimumPriceError = Object.values(fieldErrors).some(
    (message) =>
      message?.startsWith('O valor mínimo deste produto') ||
      message?.startsWith('A entrada paga precisa'),
  )

  return (
    <>
      <Card className={section}>
        <div>
          <h2 className={sectionTitle}>Preço & pagamento</h2>
          <p className={sectionCopy}>Escolha se o produto é pago uma vez ou por assinatura.</p>
        </div>
        {hasMinimumPriceError ? (
          <Banner tone="danger" title="Preço abaixo do mínimo">
            Use pelo menos{' '}
            {formatBuyerPrice(money(minimumProductBasePriceAmount, product.price.currency))} em cada
            cobrança: oferta, assinatura, entrada paga, order bump e etapa do funil.
          </Banner>
        ) : null}
        {fieldErrors.split ? (
          <div id="field-split" tabIndex={-1}>
            <Banner tone="danger" title={fieldErrors.split}>
              {fieldErrors.split === feePreviewValidationError ? (
                <>
                  Não conseguimos confirmar a taxa deste preço.{' '}
                  <Button size="sm" variant="secondary" onClick={feePreviewState.onRetry}>
                    Tentar novamente
                  </Button>
                </>
              ) : (
                'Revise o preço e os percentuais de afiliado e coprodutor antes de salvar.'
              )}
            </Banner>
          </div>
        ) : null}
        <GlobalPricingNotice product={product} />
        <StatementDescriptorField
          product={product}
          patch={patch}
          fieldErrors={fieldErrors}
          clearFieldErrors={clearFieldErrors}
        />
        <div className={segmentedField}>
          <span className={segmentedLabel}>Modelo de cobrança</span>
          <SegmentedControl
            ariaLabel="Modelo de cobrança"
            value={product.priceModel}
            options={[
              { value: 'one_time', label: 'Pagamento único' },
              { value: 'subscription', label: 'Assinatura' },
            ]}
            onChange={(value) => patch((current) => applyBillingModel(current, value))}
          />
        </div>
        {product.priceModel === 'subscription'
          ? renderSubscriptionPricing(
              product,
              patch,
              fieldErrors,
              clearFieldErrors,
              minuteBillingEnabled,
            )
          : renderOneTimePricing(product, patch, fieldErrors, clearFieldErrors)}
      </Card>
    </>
  )
}

interface FeePreviewState {
  readonly data?: FeePreview
  readonly isEnabled: boolean
  readonly isLoading: boolean
  readonly isError: boolean
  readonly onRetry: () => void
}

function FeePolicyPreview({ state }: { state: FeePreviewState }) {
  if (!state.isEnabled) {
    return (
      <Banner tone="info" title="Informe um preço válido">
        A taxa efetiva será consultada quando houver um valor positivo.
      </Banner>
    )
  }

  if (state.isLoading) {
    return (
      <Banner tone="info" title="Calculando a taxa efetiva">
        Consultando o plano deste produtor. Nenhum valor é estimado no navegador.
      </Banner>
    )
  }

  if (state.isError || !state.data) {
    return (
      <Banner tone="warning" title="Não foi possível consultar a taxa">
        O produto não pode ser salvo sem validar a taxa efetiva.{' '}
        <Button size="sm" variant="secondary" onClick={state.onRetry}>
          Tentar novamente
        </Button>
      </Banner>
    )
  }

  const fee = state.data
  const fixedFeeIsZero = Money.fromMonetaryValue(fee.fixedFee).isZero()
  const title =
    fee.mode === 'MICRO' ? 'Microtransação: a menor taxa foi aplicada' : 'Taxa padrão da venda'

  return (
    <Banner tone={fee.mode === 'MICRO' ? 'success' : 'info'} title={title}>
      <div>
        <strong>{formatPercentageLabel(parseDecimal(fee.percentage))}</strong> sobre{' '}
        {formatMoneyValue(fee.amount)} = {formatMoneyValue(fee.percentageFee)}
        {fixedFeeIsZero ? ' · sem taxa fixa' : ` + fixa ${formatMoneyValue(fee.fixedFee)}`}.
      </div>
      <div>
        Taxa Vendepay: <strong>{formatMoneyValue(fee.totalFee)}</strong> · líquido antes de reserva
        e participantes: <strong>{formatMoneyValue(fee.netAmount)}</strong>.
      </div>
      {fee.mode === 'MICRO' ? (
        <div>
          A regra padrão cobraria {formatMoneyValue(fee.standardFee)}; o sistema escolheu a menor.
        </div>
      ) : null}
      <div>
        Piso operacional: {formatMoneyValue(fee.minimumSellablePrice)} · plano{' '}
        <strong>{fee.planId}</strong>. A fixa permanece na moeda da venda.
      </div>
    </Banner>
  )
}

function renderOneTimePricing(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
) {
  const available = availablePaymentMethods(product.paymentMethods)
  const cardOn = available.includes('CREDIT_CARD')

  function toggleMethod(method: PaymentMethod) {
    patch((current) => {
      const list = availablePaymentMethods(current.paymentMethods)
      const has = list.includes(method)
      if (has && list.length === 1) return current
      return {
        ...current,
        paymentMethods: has ? list.filter((item) => item !== method) : [...list, method],
      }
    })
  }

  function updateOffer(offerId: string, input: Partial<Offer>) {
    patch((current) => ({
      ...current,
      offers: current.offers.map((offer) =>
        offer.id === offerId ? { ...offer, ...input } : offer,
      ),
    }))
  }

  function updatePrimaryOffer(input: Partial<Offer>) {
    patch((current) => {
      const currentPrimaryOfferId = primaryOfferIdFor(current)
      const targetOfferId = currentPrimaryOfferId || current.offers[0]?.id || ''
      if (current.offers.length > 0) {
        return {
          ...current,
          offers: current.offers.map((offer) =>
            offer.id === targetOfferId ? { ...offer, ...input } : offer,
          ),
          links: { ...current.links, primaryOfferId: targetOfferId },
        }
      }

      const newOfferId = makeId('offer')
      return {
        ...current,
        offers: [
          {
            id: newOfferId,
            name: 'Oferta principal',
            price: current.price,
            status: 'ACTIVE',
            ...input,
          },
        ],
        links: { ...current.links, primaryOfferId: newOfferId },
      }
    })
  }

  function addOffer() {
    patch((current) => ({
      ...current,
      offers: [
        ...current.offers,
        {
          id: makeId('offer'),
          name: `Oferta ${current.offers.length + 1}`,
          price: money('97.000', current.price.currency),
          status: 'ACTIVE',
        },
      ],
    }))
  }

  function removeOffer(offerId: string) {
    patch((current) => {
      const offers = current.offers.filter((offer) => offer.id !== offerId)
      const primaryOfferId =
        primaryOfferIdFor(current) === offerId ? offers[0]?.id || '' : current.links.primaryOfferId
      return {
        ...current,
        offers,
        links: { ...current.links, primaryOfferId },
      }
    })
  }

  const primaryOfferId = primaryOfferIdFor(product)
  const offers =
    product.offers.length > 0
      ? editableOffers(product)
      : [{ id: '', name: 'Oferta principal', price: product.price, status: 'ACTIVE' as const }]

  return (
    <>
      <div className={sentenceCard}>
        <div className={sentenceText}>
          Aceita no{' '}
          <span className={methodTagSoon} aria-disabled="true" title="Pix em breve">
            Pix <span className={methodTagSoonNote}>em breve</span>
          </span>{' '}
          <span className={methodTagSoon} aria-disabled="true" title="Boleto em breve">
            Boleto <span className={methodTagSoonNote}>em breve</span>
          </span>{' '}
          ou no{' '}
          <button
            type="button"
            className={methodTag({ active: cardOn })}
            aria-pressed={cardOn}
            onClick={() => toggleMethod('CREDIT_CARD')}
          >
            Cartão de crédito
          </button>
          .
        </div>
        <div className={sentenceResolveRow}>
          <CornerDownRight size={15} />
          <span className={sentenceResolveText}>
            As formas de pagamento valem para todas as ofertas deste produto.
          </span>
        </div>
      </div>
      <p className={sentenceHint}>
        Toque nos <strong>métodos verdes</strong> para ligar ou desligar.
      </p>

      <div className={sectionHeader}>
        <div>
          <h3 className={sectionTitle}>
            {MAX_OFFERS_PER_PRODUCT > 1 ? 'Ofertas de produto' : 'Preço do produto'}
          </h3>
          <p className={sectionCopy}>
            {MAX_OFFERS_PER_PRODUCT > 1
              ? 'Cada oferta gera um checkout com um valor para o seu produto principal.'
              : 'O produto tem um preço. Para vender outro valor, crie outro produto.'}
          </p>
        </div>
        {canAddOffer(product) ? (
          <Button leftIcon={<Plus size={16} />} onClick={addOffer}>
            Nova oferta
          </Button>
        ) : null}
      </div>

      {offers.map((offer, index) => {
        const isPrimary =
          offer.id === primaryOfferId || (primaryOfferId.length === 0 && index === 0)
        const priceError =
          fieldErrors[`offers.${offer.id}.price.amount`] ??
          (isPrimary ? fieldErrors['price.amount'] : undefined)
        const applyPrice = (amount: string) => {
          const offerIndex = product.offers.findIndex((candidate) => candidate.id === offer.id)
          clearFieldErrors(
            `offers.${offer.id}.price.amount`,
            ...(offerIndex >= 0 ? [`offers.${offerIndex}.price.amount`] : []),
            ...(isPrimary ? ['price.amount', 'offers.0.price.amount', 'split'] : []),
          )
          if (isPrimary) {
            patch((current) =>
              syncPrimaryOfferPrice(current, money(amount, current.price.currency)),
            )
          } else {
            updateOffer(offer.id, { price: money(amount, offer.price.currency) })
          }
        }
        return (
          <div key={offer.id || 'primary'} className={rowCard}>
            <div className={editorStack}>
              {MAX_OFFERS_PER_PRODUCT > 1 ? (
                <div className={sectionHeader}>
                  <div className={inlineRow}>
                    <span className={planCardIcon}>
                      <Tag size={16} />
                    </span>
                    <input
                      className={planNameInput}
                      value={offer.name}
                      aria-label="Nome da oferta"
                      onChange={(event) =>
                        isPrimary
                          ? updatePrimaryOffer({ name: event.target.value })
                          : updateOffer(offer.id, { name: event.target.value })
                      }
                    />
                  </div>
                  <div className={actionRow}>
                    <span className={muted}>
                      {offer.status === 'ACTIVE' ? 'Oferta ativa' : 'Oferta inativa'}
                    </span>
                    <Toggle
                      checked={offer.status === 'ACTIVE'}
                      onChange={(active) =>
                        isPrimary
                          ? updatePrimaryOffer({ status: active ? 'ACTIVE' : 'INACTIVE' })
                          : updateOffer(offer.id, { status: active ? 'ACTIVE' : 'INACTIVE' })
                      }
                      ariaLabel={`Status da ${offer.name || 'oferta'}`}
                    />
                    {isPrimary ? null : (
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Remover ${offer.name || 'oferta'}`}
                        onClick={() => removeOffer(offer.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ) : null}
              <div className={sentenceCard}>
                <div className={sentenceText}>
                  Cobrar{' '}
                  <EditableChip
                    label={formatBuyerPrice(offer.price)}
                    ariaLabel="Editar valor da oferta"
                  >
                    {(close) => (
                      <>
                        <span className={sentencePopoverTitle}>Valor da oferta</span>
                        <CurrencyField
                          currency={offer.price.currency}
                          value={offer.price.amount}
                          error={priceError}
                          onValueChange={applyPrice}
                        />
                        <div className={presetRow}>
                          {['47.000', '97.000', '197.000'].map((amount) => (
                            <button
                              key={amount}
                              type="button"
                              className={presetChip}
                              onClick={() => applyPrice(amount)}
                            >
                              {formatMoneyValue(money(amount, offer.price.currency))}
                            </button>
                          ))}
                        </div>
                        <Button size="sm" onClick={close}>
                          Pronto
                        </Button>
                      </>
                    )}
                  </EditableChip>
                  .
                </div>
                <div className={sentenceResolveRow}>
                  <CornerDownRight size={15} />
                  <span className={sentenceResolveText}>
                    {isPrimary ? 'Link padrão do produto.' : 'Gera um link próprio no checkout.'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}

function renderSubscriptionPricing(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
  minuteBillingEnabled: boolean,
) {
  return renderRecurrencePlans(product, patch, fieldErrors, clearFieldErrors, minuteBillingEnabled)
}

function StatementDescriptorField({
  product,
  patch,
  fieldErrors,
  clearFieldErrors,
}: {
  product: Product
  patch: (updater: (current: Product) => Product) => void
  fieldErrors: ProductEditorFieldErrors
  clearFieldErrors: (...paths: string[]) => void
}) {
  return (
    <TextField
      id="field-statementDescriptor"
      label="Descrição na fatura"
      maxLength={22}
      required
      error={fieldErrors.statementDescriptor}
      value={product.statementDescriptor}
      onChange={(event) => {
        clearFieldErrors('statementDescriptor')
        patch((current) => ({
          ...current,
          statementDescriptor: sanitizeStatementDescriptor(event.target.value),
        }))
      }}
    />
  )
}

function DeliveryField({
  product,
  patch,
}: {
  product: Product
  patch: (updater: (current: Product) => Product) => void
}) {
  return (
    <SelectField
      label="Entrega"
      options={deliveryOptions}
      value={product.deliveryType}
      onChange={(event) =>
        patch((current) => ({
          ...current,
          deliveryType: event.target.value as Product['deliveryType'],
        }))
      }
    />
  )
}

function renderFunnel(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
) {
  return (
    <FunnelEditor
      key={product.id}
      product={product}
      patch={patch}
      fieldErrors={fieldErrors}
      clearFieldErrors={clearFieldErrors}
    />
  )
}

function FunnelEditor({
  product,
  patch,
  fieldErrors,
  clearFieldErrors,
}: {
  product: Product
  patch: (updater: (current: Product) => Product) => void
  fieldErrors: ProductEditorFieldErrors
  clearFieldErrors: (...paths: string[]) => void
}) {
  const [stageDialog, setStageDialog] = useState<FunnelStageDialogState | null>(null)
  const [selectedId, setSelectedId] = useState<string>(FUNNEL_ROOT_ID)
  const openedErrorRef = useRef('')
  const productsResult = useProductEditorProductsQuery()
  const offerProducts = useMemo(
    () => getOfferProductOptions(product, productsResult.data, funnelSelectionsOf(product)),
    [product, productsResult.data],
  )

  const funnelNodes = useMemo(() => sanitizeFunnelNodes(product.funnelNodes), [product.funnelNodes])
  const layout = useMemo(() => layoutFunnelTree(funnelNodes), [funnelNodes])
  const bumps = product.orderBumps
  const baseTicket = product.price
  const maxTicket = sumSameCurrency(product.price, [
    ...bumps.map((bump) => bump.price),
    ...funnelNodes.map((node) => node.price),
  ])
  const activeStages = bumps.length + funnelNodes.length
  const hasOffers = funnelNodes.length > 0
  const baseAmount = Money.fromMonetaryValue(baseTicket).toNumber()
  const maxAmount = Money.fromMonetaryValue(maxTicket).toNumber()
  const upliftLabel =
    baseAmount > 0 && maxAmount > baseAmount
      ? `+${Math.round(((maxAmount - baseAmount) / baseAmount) * 100)}% no ticket máximo`
      : 'Sem ofertas no funil'

  useEffect(() => {
    if (stageDialog !== null) return
    const invalidBump = bumps.find(
      (bump) => fieldErrors[`orderBumps.${bump.id}.price.amount`] !== undefined,
    )
    const invalidNode = funnelNodes.find(
      (node) => fieldErrors[`funnelNodes.${node.id}.price.amount`] !== undefined,
    )
    const signature = invalidBump
      ? `order_bump:${invalidBump.id}`
      : invalidNode
        ? `${invalidNode.kind}:${invalidNode.id}`
        : ''

    if (!signature) {
      openedErrorRef.current = ''
      return
    }
    if (openedErrorRef.current === signature) return
    openedErrorRef.current = signature

    const frame = window.requestAnimationFrame(() => {
      if (invalidBump) {
        setSelectedId(FUNNEL_ROOT_ID)
        setStageDialog({ kind: 'order_bump', id: invalidBump.id })
        return
      }
      if (invalidNode) {
        setSelectedId(invalidNode.id)
        setStageDialog({
          kind: invalidNode.kind,
          id: invalidNode.id,
          parentId: invalidNode.parentId,
          branch: invalidNode.branch,
        })
      }
    })
    return () => window.cancelAnimationFrame(frame)
  }, [bumps, fieldErrors, funnelNodes, stageDialog])

  const openBranchStage = useCallback((parentId: string, branch: FunnelBranch) => {
    setSelectedId(parentId)
    setStageDialog({ kind: branch === 'reject' ? 'downsell' : 'upsell', parentId, branch })
  }, [])

  const editNode = useCallback((node: FunnelNode) => {
    setSelectedId(node.id)
    setStageDialog({ kind: node.kind, id: node.id, parentId: node.parentId, branch: node.branch })
  }, [])

  const addBump = useCallback(() => {
    setSelectedId(FUNNEL_ROOT_ID)
    setStageDialog({ kind: 'order_bump' })
  }, [])

  const editBump = useCallback((id: string) => {
    setSelectedId(FUNNEL_ROOT_ID)
    setStageDialog({ kind: 'order_bump', id })
  }, [])

  function removeBump(id: string) {
    patch((current) => ({
      ...current,
      orderBumps: current.orderBumps.filter((item) => item.id !== id),
    }))
  }

  function addStepFromSelected() {
    if (selectedId !== 'success') {
      const branches =
        selectedId === FUNNEL_ROOT_ID ? (['accept'] as FunnelBranch[]) : FUNNEL_BRANCHES
      const [firstFree] = getFreeBranches(funnelNodes, selectedId, branches)
      if (firstFree) {
        openBranchStage(selectedId, firstFree)
        return
      }
    }
    let cursor = FUNNEL_ROOT_ID
    for (;;) {
      const next = getBranchChild(funnelNodes, cursor, 'accept')
      if (!next) break
      cursor = next.id
    }
    openBranchStage(cursor, 'accept')
  }

  function saveStage(draft: FunnelStageDraft) {
    patch((current) => {
      if (draft.kind === 'order_bump') {
        return { ...current, orderBumps: upsertOrderBump(current.orderBumps, draft) }
      }
      return {
        ...current,
        funnelNodes: upsertFunnelNode(sanitizeFunnelNodes(current.funnelNodes), draft),
      }
    })
    setStageDialog(null)
  }

  function removeStage(state: FunnelStageDialogState) {
    const id = state.id
    if (!id) return
    patch((current) => {
      if (state.kind === 'order_bump') {
        return { ...current, orderBumps: current.orderBumps.filter((item) => item.id !== id) }
      }
      const remove = new Set([id, ...collectDescendantIds(current.funnelNodes, id)])
      return { ...current, funnelNodes: current.funnelNodes.filter((item) => !remove.has(item.id)) }
    })
    if (id === selectedId) setSelectedId(FUNNEL_ROOT_ID)
    setStageDialog(null)
  }

  const derivedNodes = useMemo<FunnelFlowNodeType[]>(() => {
    const list: FunnelFlowNodeType[] = []
    list.push({
      id: FUNNEL_ROOT_ID,
      type: 'funnelNode',
      position: layout.positions.get(FUNNEL_ROOT_ID) ?? { x: 0, y: 0 },
      data: {
        tone: 'checkout',
        badge: 'Checkout',
        title: product.name,
        productLabel: 'Produto principal',
        price: mainProductPriceLabel(product),
        bumps: bumps.map((bump) => {
          const bumpOption = findOptionForSelection(offerProducts, bump.targetOfferId, bump.planId)
          const bumpPlan = bump.planId !== null ? (bumpOption?.plan ?? null) : null
          return {
            id: bump.id,
            title: bump.title,
            value: bumpPlan ? formatMoneyValue(bumpPlan.value) : formatMoneyValue(bump.price),
            ...(bumpPlan ? { valueSuffix: `/${intervalLabel(bumpPlan)}` } : {}),
          }
        }),
        meta: mainProductMeta(product),
        selected: selectedId === FUNNEL_ROOT_ID,
        freeBranches: getFreeBranches(funnelNodes, FUNNEL_ROOT_ID, ['accept']),
        onAdd: (branch) => openBranchStage(FUNNEL_ROOT_ID, branch),
        onAddBump: addBump,
        onEditBump: editBump,
      },
    })

    funnelNodes.forEach((node) => {
      const position = layout.positions.get(node.id)
      if (!position) return
      const option = findOptionForSelection(offerProducts, node.offerId, node.planId)
      const nodePlan = node.planId !== null ? (option?.plan ?? null) : null
      list.push({
        id: `funnel:${node.id}`,
        type: 'funnelNode',
        position,
        data: {
          tone: node.kind,
          badge: node.kind === 'upsell' ? 'Upsell' : 'Downsell',
          title: node.title,
          productLabel: option ? option.product.name : 'Oferta',
          price: nodePlan ? planPriceLabel(nodePlan) : formatMoneyValue(node.price),
          meta: `${node.kind === 'upsell' ? 'Oferta complementar' : 'Oferta alternativa'}${
            nodePlan ? ` · Assinatura · ${nodePlan.name}` : node.planId ? ' · Assinatura' : ''
          }`,
          hostingLabel: node.mode === 'hosted' ? 'Página VendePay' : 'Sua página',
          hint: 'Clique para editar',
          selected: selectedId === node.id,
          freeBranches: getFreeBranches(funnelNodes, node.id, FUNNEL_BRANCHES),
          onAdd: (branch) => openBranchStage(node.id, branch),
        },
      })
    })

    if (hasOffers) {
      list.push({
        id: 'success',
        type: 'funnelNode',
        position: {
          x: (layout.maxDepth + 1) * FUNNEL_COLUMN,
          y: ((layout.lanes - 1) / 2) * FUNNEL_ROW,
        },
        data: {
          tone: 'success',
          badge: 'Obrigado',
          title: 'Página de sucesso',
          meta: 'Acesso liberado e entrega do produto.',
          selected: selectedId === 'success',
        },
      })
    }

    return list
  }, [
    funnelNodes,
    layout,
    offerProducts,
    bumps,
    product,
    selectedId,
    hasOffers,
    openBranchStage,
    addBump,
    editBump,
  ])

  const [flowNodes, setFlowNodes, onFlowNodesChange] = useNodesState<FunnelFlowNodeType>([])
  const draggedIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    setFlowNodes((previous) => {
      const positionById = new Map(previous.map((node) => [node.id, node.position]))
      return derivedNodes.map((node) => {
        const moved = positionById.get(node.id)
        return draggedIds.current.has(node.id) && moved ? { ...node, position: moved } : node
      })
    })
  }, [derivedNodes, setFlowNodes])

  function autoArrange() {
    draggedIds.current.clear()
    setFlowNodes(derivedNodes.map((node) => ({ ...node })))
  }

  const edges: Edge[] = []
  function pushEdge(
    sourceId: string,
    targetId: string,
    branch: FunnelBranch,
    options: { label?: string; terminal?: boolean } = {},
  ) {
    edges.push({
      id: `edge:${sourceId}->${targetId}:${branch}`,
      source: sourceId,
      target: targetId,
      type: 'smoothstep',
      ...(options.label ? { label: options.label } : {}),
      animated: !options.terminal && branch === 'accept',
      ...(options.terminal ? terminalEdge : branch === 'reject' ? refusedEdge : acceptedEdge),
    })
  }

  const checkoutChild = getBranchChild(funnelNodes, FUNNEL_ROOT_ID, 'accept')
  if (checkoutChild) {
    pushEdge(FUNNEL_ROOT_ID, `funnel:${checkoutChild.id}`, 'accept', { label: 'pós-compra' })
  }

  funnelNodes.forEach((node) => {
    FUNNEL_BRANCHES.forEach((branch) => {
      const child = getBranchChild(funnelNodes, node.id, branch)
      if (child) {
        pushEdge(`funnel:${node.id}`, `funnel:${child.id}`, branch)
      }
    })
    if (hasOffers) {
      const free = getFreeBranches(funnelNodes, node.id, FUNNEL_BRANCHES)
      if (free.length > 0) {
        const branch: FunnelBranch = free.includes('accept') ? 'accept' : 'reject'
        pushEdge(`funnel:${node.id}`, 'success', branch, { label: 'fim', terminal: true })
      }
    }
  })

  const funnelPaymentLabel =
    availablePaymentMethods(product.paymentMethods)
      .map(
        (method) =>
          paymentMethods.find((item) => item.value === method)?.label ?? paymentMethodLabel(method),
      )
      .join(', ') || 'Cartão'

  return (
    <Card className={section}>
      <div className={sectionHeader}>
        <div>
          <h2 className={sectionTitle}>Funil de vendas</h2>
          <p className={sectionCopy}>
            O upsell e o downsell aparecem <strong>depois da compra</strong>, em 1 clique. Ao
            terminar uma etapa sem próxima oferta, o comprador segue para a página de sucesso.
          </p>
        </div>
        <div className={actionRow}>
          <Button variant="secondary" onClick={autoArrange}>
            Reorganizar
          </Button>
          <Button leftIcon={<Plus size={16} />} onClick={addStepFromSelected}>
            Adicionar etapa
          </Button>
        </div>
      </div>
      <div className={funnelPaymentNotice}>
        <CreditCard size={16} className={funnelPaymentNoticeIcon} />
        <div className={funnelPaymentNoticeBody}>
          <span className={funnelPaymentNoticeStrong}>
            As formas de pagamento de todo o funil seguem o produto da oferta principal.
          </span>
          <span>
            Este funil aceita:{' '}
            <span className={funnelPaymentNoticeStrong}>{funnelPaymentLabel}</span>. Order bumps
            herdam essas formas; upsells e downsells em 1 clique só rodam para quem pagar no{' '}
            <span className={funnelPaymentNoticeStrong}>cartão</span> (o cartão salvo é recobrado;
            PIX e boleto não têm o que recobrar).
          </span>
        </div>
      </div>
      <div className={funnelHint}>
        <GitFork size={15} />
        Use os botões dos cards para criar upsells complementares ou downsells alternativos. “Página
        de sucesso” é apenas o fim do caminho quando falta outra oferta configurada.
      </div>
      <div className={funnelLegend}>
        <span className={funnelLegendTitle}>
          <GitFork size={20} />
          Caminho pós-compra
        </span>
        <span className={funnelLegendMeta}>
          <span>━ upsell</span>
          <span>┄ downsell</span>
          <span>┄ fim</span>
          <strong>{upliftLabel}</strong>
        </span>
      </div>
      <div className={funnelCanvas}>
        <div className={funnelFlow}>
          <ReactFlow
            nodes={flowNodes}
            edges={edges}
            nodeTypes={funnelNodeTypes}
            onNodesChange={onFlowNodesChange}
            onNodeDragStop={(_, node) => draggedIds.current.add(node.id)}
            nodesDraggable
            nodesConnectable={false}
            fitView
            fitViewOptions={{ padding: 0.25 }}
            minZoom={0.2}
            proOptions={{ hideAttribution: true }}
            onNodeClick={(_, node) => {
              if (node.id === FUNNEL_ROOT_ID) {
                setSelectedId(FUNNEL_ROOT_ID)
                return
              }
              if (node.id === 'success') {
                setSelectedId('success')
                return
              }
              const funnelNode = funnelNodes.find((item) => `funnel:${item.id}` === node.id)
              if (funnelNode) editNode(funnelNode)
            }}
          >
            <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
          </ReactFlow>
        </div>
      </div>
      <div className={funnelMetricGrid}>
        <div className={funnelMetric({ emphasis: false })}>
          <span className={funnelMetricLabel}>Ticket sem funil</span>
          <strong className={funnelMetricValue}>{formatMoneyValue(baseTicket)}</strong>
        </div>
        <div className={funnelMetric({ emphasis: true })}>
          <span className={funnelMetricLabel}>Ticket máximo possível</span>
          <strong className={funnelMetricValue}>{formatMoneyValue(maxTicket)}</strong>
        </div>
        <div className={funnelMetric({ emphasis: false })}>
          <span className={funnelMetricLabel}>Etapas ativas</span>
          <strong className={funnelMetricValue}>{activeStages}</strong>
        </div>
      </div>
      <div className={sectionHeader}>
        <div>
          <div className={inlineRow}>
            <h3 className={sectionTitle}>Order bumps do checkout</h3>
            <StatusBadge
              tone="informative"
              size="sm"
              icon={<ShoppingCart size={12} />}
              label="No checkout · antes de pagar"
            />
          </div>
          <p className={sectionCopy}>
            Ofertas extras exibidas dentro do checkout principal, antes do pagamento. Não aparecem
            nos upsells nem downsells.
          </p>
        </div>
        <Button variant="secondary" leftIcon={<Plus size={16} />} onClick={addBump}>
          Adicionar order bump
        </Button>
      </div>
      <div className={tableLike}>
        {bumps.length === 0 ? (
          <div className={compactCard}>
            <span className={featureTitle}>Nenhum order bump</span>
            <p className={muted}>
              Crie uma oferta-relâmpago que aparece dentro do checkout, sem sair da compra.
            </p>
          </div>
        ) : (
          bumps.map((bump) => (
            <div key={bump.id} className={rowCard}>
              <div>
                <span className={nodeTitle}>{bump.title}</span>
                <p className={muted}>
                  {formatMoneyValue(bump.price)} ·{' '}
                  {bump.commissionable ? 'comissionável' : 'sem comissão'}
                </p>
              </div>
              <div className={actionRow}>
                <Button variant="secondary" onClick={() => editBump(bump.id)}>
                  Editar
                </Button>
                <Button
                  variant="secondary"
                  aria-label="Remover order bump"
                  onClick={() => removeBump(bump.id)}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
      {stageDialog ? (
        <FunnelStageModal
          state={stageDialog}
          product={product}
          offerProducts={offerProducts}
          priceError={
            stageDialog.id
              ? stageDialog.kind === 'order_bump'
                ? fieldErrors[`orderBumps.${stageDialog.id}.price.amount`]
                : fieldErrors[`funnelNodes.${stageDialog.id}.price.amount`]
              : undefined
          }
          priceFieldPath={
            stageDialog.id
              ? stageDialog.kind === 'order_bump'
                ? `orderBumps.${stageDialog.id}.price.amount`
                : `funnelNodes.${stageDialog.id}.price.amount`
              : undefined
          }
          onPriceChange={() => {
            if (!stageDialog.id) return
            if (stageDialog.kind === 'order_bump') {
              const index = product.orderBumps.findIndex((item) => item.id === stageDialog.id)
              clearFieldErrors(
                `orderBumps.${stageDialog.id}.price.amount`,
                ...(index >= 0 ? [`orderBumps.${index}.price.amount`] : []),
              )
              return
            }
            const index = product.funnelNodes.findIndex((item) => item.id === stageDialog.id)
            clearFieldErrors(
              `funnelNodes.${stageDialog.id}.price.amount`,
              ...(index >= 0 ? [`funnelNodes.${index}.price.amount`] : []),
            )
          }}
          onClose={() => setStageDialog(null)}
          onSave={saveStage}
          onRemove={removeStage}
        />
      ) : null}
    </Card>
  )
}

function upsertOrderBump(orderBumps: OrderBump[], draft: FunnelStageDraft) {
  const nextBump: OrderBump = {
    id: draft.id ?? makeId('bump'),
    targetOfferId: draft.offerId,
    planId: draft.planId,
    title: draft.title,
    description: draft.description,
    callToAction: draft.acceptLabel,
    price: draft.price,
    commissionable: true,
    imageUrl: draft.bumpImageUrl.trim(),
  }

  if (draft.previousKind === 'order_bump' && draft.id) {
    return orderBumps.map((item) => (item.id === draft.id ? nextBump : item))
  }

  return [...orderBumps, nextBump]
}

function upsertFunnelNode(funnelNodes: FunnelNode[], draft: FunnelStageDraft) {
  const nodeKind = draft.kind === 'order_bump' ? 'upsell' : draft.kind
  const existing = draft.id ? funnelNodes.find((item) => item.id === draft.id) : undefined
  const nextNode: FunnelNode = {
    id: draft.id ?? makeId(nodeKind),
    kind: nodeKind,
    parentId: draft.parentId,
    branch: draft.branch,
    title: draft.title,
    subtext: draft.subtext,
    mediaUrl: draft.mediaUrl,
    offerId: draft.offerId,
    price: draft.price,
    acceptLabel: draft.acceptLabel,
    rejectLabel: draft.rejectLabel,
    mode: draft.mode,
    snippet: draft.mode === 'snippet' ? buildUpsellSnippet(draft.offerId) : '',
    commissionable: draft.commissionable,
    planId: draft.planId,
  }

  if (existing) {
    return funnelNodes.map((item) => (item.id === draft.id ? nextNode : item))
  }

  return [...funnelNodes, nextNode]
}

function FunnelStageModal({
  state,
  product,
  offerProducts,
  priceError,
  priceFieldPath,
  onPriceChange,
  onClose,
  onSave,
  onRemove,
}: {
  state: FunnelStageDialogState
  product: Product
  offerProducts: OfferProductOption[]
  priceError?: string
  priceFieldPath?: string
  onPriceChange: () => void
  onClose: () => void
  onSave: (draft: FunnelStageDraft) => void
  onRemove: (state: FunnelStageDialogState) => void
}) {
  const existingBump =
    state.kind === 'order_bump' && state.id
      ? product.orderBumps.find((item) => item.id === state.id)
      : undefined
  const existingNode =
    state.kind !== 'order_bump' && state.id
      ? product.funnelNodes.find((item) => item.id === state.id)
      : undefined
  const initialOption =
    existingBump || existingNode
      ? findOptionForSelection(
          offerProducts,
          existingBump?.targetOfferId ?? existingNode?.offerId ?? '',
          existingBump?.planId ?? existingNode?.planId ?? null,
        )
      : offerProducts[0]
  const kind = state.kind
  const [selection, setSelection] = useState(initialOption?.value ?? '')
  const [priceAmount, setPriceAmount] = useState(
    initialOption?.plan?.value.amount ??
      existingBump?.price.amount ??
      existingNode?.price.amount ??
      initialOption?.price.amount ??
      '97.000',
  )
  const [discount, setDiscount] = useState('0')
  const [title, setTitle] = useState(
    existingBump?.title ?? existingNode?.title ?? initialOption?.product.name ?? product.name,
  )
  const [description, setDescription] = useState(
    existingBump?.description ?? 'Oferta configurada para aumentar o ticket no fluxo de compra.',
  )
  const [acceptLabel, setAcceptLabel] = useState(
    existingBump?.callToAction ?? existingNode?.acceptLabel ?? 'Sim, quero',
  )
  const [rejectLabel, setRejectLabel] = useState(existingNode?.rejectLabel ?? 'Não, obrigado')
  const [mode, setMode] = useState<FunnelNode['mode']>(
    HOSTED_OFFER_ENABLED ? (existingNode?.mode ?? 'hosted') : 'snippet',
  )
  const [subtext, setSubtext] = useState(existingNode?.subtext ?? '')
  const [mediaUrl, setMediaUrl] = useState(existingNode?.mediaUrl ?? '')
  const [bumpImageUrl, setBumpImageUrl] = useState(existingBump?.imageUrl ?? '')
  const [mediaMode, setMediaMode] = useState<'image' | 'video'>(
    detectMediaKind(existingNode?.mediaUrl ?? '') === 'video' ? 'video' : 'image',
  )
  const commissionable = existingNode?.commissionable ?? true
  const parentId = state.parentId ?? existingNode?.parentId ?? FUNNEL_ROOT_ID
  const branch: FunnelBranch = state.branch ?? existingNode?.branch ?? 'accept'
  const selectedOption = findOfferProductOption(offerProducts, selection)
  const selectedCurrency = selectedOption?.price.currency ?? product.price.currency
  const selectedProduct = selectedOption?.product ?? product
  const isBump = kind === 'order_bump'
  const planId = selectedOption?.planId ?? null
  const selectedPlan = selectedOption?.plan ?? null
  const stageLabel = funnelStageLabels[kind]
  const editing = Boolean(state.id)
  const canCommissionAffiliateOnOffer =
    !isBump && planId === null && selectedProduct.affiliate.enabled
  const snippetText = buildUpsellSnippet(selectedOption?.offerId ?? '')

  function copySnippet() {
    void navigator.clipboard?.writeText(snippetText)
    toast.success('Código copiado.')
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  function handleOfferChange(nextValue: string) {
    onPriceChange()
    const nextOption = findOfferProductOption(offerProducts, nextValue)
    setSelection(nextOption?.value ?? nextValue)
    if (nextOption) {
      setPriceAmount(
        nextOption.plan
          ? nextOption.plan.value.amount
          : getDiscountedPrice(nextOption.price, discount).amount,
      )
      if (!editing) {
        setTitle(nextOption.product.name)
      }
    }
  }

  function handleDiscountChange(value: string) {
    onPriceChange()
    setDiscount(value)
    if (selectedOption) {
      setPriceAmount(getDiscountedPrice(selectedOption.price, value).amount)
    }
  }

  function handleSave() {
    onSave({
      kind,
      id: state.id,
      previousKind: state.kind,
      parentId,
      branch,
      offerId: selectedOption?.offerId ?? '',
      price: selectedPlan
        ? money(selectedPlan.value.amount, selectedPlan.value.currency)
        : money(priceAmount, selectedCurrency),
      title: title.trim() || selectedProduct.name,
      subtext: subtext.trim(),
      description: description.trim(),
      acceptLabel: acceptLabel.trim() || 'Sim, quero',
      rejectLabel: rejectLabel.trim() || 'Não, obrigado',
      mode,
      mediaUrl: mediaUrl.trim(),
      bumpImageUrl: bumpImageUrl.trim(),
      commissionable: isBump ? true : canCommissionAffiliateOnOffer ? commissionable : false,
      planId,
    })
  }

  return createPortal(
    <div className={funnelStageBackdrop} role="presentation" onClick={onClose}>
      <aside
        className={funnelStagePanel}
        role="dialog"
        aria-modal="true"
        aria-label={stageLabel.title}
        onClick={(event) => event.stopPropagation()}
      >
        <header className={funnelStageHeader}>
          <span className={funnelStageIcon}>
            <GitFork size={20} />
          </span>
          <div className={funnelStageTitleStack}>
            <h3 className={funnelStageTitle}>{stageLabel.title}</h3>
            <span className={funnelStageSubtitle}>{stageLabel.subtitle}</span>
            <StatusBadge
              tone="informative"
              size="sm"
              icon={isBump ? <ShoppingCart size={12} /> : <TrendingUp size={12} />}
              label={isBump ? 'No checkout · antes de pagar' : 'Pós-compra · 1 clique'}
            />
          </div>
          <button type="button" className={funnelStageClose} onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>

        <div className={funnelStageBody}>
          <SelectField
            label="Oferta (produto)"
            value={selection}
            options={offerProducts.map((option) => ({
              label: `${option.label} · ${option.priceLabel}`,
              value: option.value,
            }))}
            onChange={(event) => handleOfferChange(event.target.value)}
          />

          <div className={offerProductCard}>
            <span className={offerProductAvatar}>
              {selectedProduct.name.slice(0, 1).toUpperCase()}
            </span>
            <div>
              <strong className={offerProductName}>{selectedProduct.name}</strong>
              <span className={offerProductMeta}>
                {productTypeLabels[selectedProduct.productType]} ·{' '}
                {selectedOption?.priceLabel ?? formatMoneyValue(selectedProduct.price)}
              </span>
            </div>
          </div>

          {planId === null ? (
            <div className={formGrid}>
              <CurrencyField
                id={priceFieldPath ? `field-${priceFieldPath}` : undefined}
                label="Preço da oferta"
                currency={selectedCurrency}
                value={priceAmount}
                error={priceError}
                autoFocus={Boolean(priceError)}
                onValueChange={(amount) => {
                  onPriceChange()
                  setPriceAmount(amount)
                }}
              />
              <PercentageField
                label="Desconto"
                value={discount}
                min={0}
                max={100}
                onValueChange={handleDiscountChange}
                onValueCommit={handleDiscountChange}
              />
            </div>
          ) : priceError ? (
            <Banner tone="danger" title="Plano abaixo do mínimo">
              {priceError} Abra o produto de origem “{selectedProduct.name}” e corrija o plano “
              {selectedPlan?.name ?? 'selecionado'}” em Preço & pagamento.
            </Banner>
          ) : (
            <div className={funnelHint}>
              <RefreshCw size={15} />
              Na assinatura, o comprador paga o valor do plano configurado em Preço & pagamento.
            </div>
          )}

          <TextField
            label="Chamada da oferta"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />

          {isBump ? (
            <>
              <TextField
                label="Descrição curta"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              <TextField
                label="Botão de aceite"
                value={acceptLabel}
                onChange={(event) => setAcceptLabel(event.target.value)}
              />
              <ImageUploadField
                label="Imagem do order bump"
                kind="bump"
                value={bumpImageUrl}
                onChange={setBumpImageUrl}
                aspect="1 / 1"
                maxInlineSize={320}
                hint="ideal 320x320"
                allowUrl
              />
              {selectedPlan ? (
                <div className={funnelHint}>
                  <RefreshCw size={15} />
                  <span>
                    Bump de assinatura ({selectedPlan.name}
                    {selectedPlan.trialEnabled && selectedPlan.trialDays > 0
                      ? selectedPlan.trialType === 'WITH_PAYMENT'
                        ? `, ${selectedPlan.trialDays} dias grátis`
                        : `, teste pago de ${selectedPlan.trialDays} dias`
                      : ''}
                    ): disponível só no cartão. O comprador vira assinante junto com a compra
                    principal e renova por {planPriceLabel(selectedPlan)}.
                  </span>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className={modalDivider} />
              <div>
                <span className={nodeTitle}>Onde a oferta aparece</span>
                <div className={offerModeGrid}>
                  <button
                    type="button"
                    className={`${offerModeCard({ selected: mode === 'hosted' })}${HOSTED_OFFER_ENABLED ? '' : ` ${offerModeDisabled}`}`}
                    disabled={!HOSTED_OFFER_ENABLED}
                    aria-disabled={!HOSTED_OFFER_ENABLED}
                    onClick={() => {
                      if (HOSTED_OFFER_ENABLED) setMode('hosted')
                    }}
                  >
                    <strong className={offerModeTitle}>
                      Página VendePay
                      {HOSTED_OFFER_ENABLED ? (
                        <span className={offerModeRecommended}>recomendado</span>
                      ) : (
                        <span className={offerModeSoon}>em breve</span>
                      )}
                    </strong>
                    <span className={offerModeDescription}>
                      Hospedamos a página da oferta. Sem código, aparece direto no checkout.
                    </span>
                  </button>
                  <button
                    type="button"
                    className={offerModeCard({ selected: mode === 'snippet' })}
                    onClick={() => setMode('snippet')}
                  >
                    <strong className={offerModeTitle}>Minha página</strong>
                    <span className={offerModeDescription}>
                      Cole um código na sua própria página de obrigado.
                    </span>
                  </button>
                </div>
                {mode === 'snippet' ? (
                  <>
                    <div className={snippetBox}>
                      <div className={snippetBoxHead}>
                        <span className={snippetBoxLabel}>cole antes de &lt;/body&gt;</span>
                        <button type="button" className={snippetCopyButton} onClick={copySnippet}>
                          <Copy size={12} />
                          copiar
                        </button>
                      </div>
                      <code className={snippetCode}>{snippetText}</code>
                    </div>
                    <div className={snippetWarn}>
                      <TriangleAlert size={15} />
                      <span>
                        Requer uma página de obrigado própria. Se o código ficar no lugar errado, a
                        oferta não aparece.
                      </span>
                    </div>
                  </>
                ) : null}
              </div>
              {mode === 'hosted' ? (
                <>
                  <TextField
                    label="Subtítulo da página"
                    value={subtext}
                    onChange={(event) => setSubtext(event.target.value)}
                  />
                  <div>
                    <span className={nodeTitle}>Mídia da oferta</span>
                    <SegmentedControl<'image' | 'video'>
                      value={mediaMode}
                      ariaLabel="Tipo de mídia da oferta"
                      options={[
                        { value: 'image', label: 'Imagem' },
                        { value: 'video', label: 'Vídeo' },
                      ]}
                      onChange={(value) => setMediaMode(value)}
                    />
                  </div>
                  {mediaMode === 'image' ? (
                    <ImageUploadField
                      label="Imagem da oferta"
                      kind="funnel"
                      value={mediaUrl}
                      onChange={setMediaUrl}
                      aspect="16 / 9"
                      hint="ideal 1280x720"
                      allowUrl
                    />
                  ) : (
                    <TextField
                      label="URL do vídeo"
                      value={mediaUrl}
                      placeholder="https://… (VTurb, Panda, YouTube)"
                      onChange={(event) => setMediaUrl(event.target.value)}
                    />
                  )}
                </>
              ) : null}
              <div className={formGrid}>
                <TextField
                  label="Botão aceitar"
                  value={acceptLabel}
                  onChange={(event) => setAcceptLabel(event.target.value)}
                />
                <TextField
                  label="Botão recusar"
                  value={rejectLabel}
                  onChange={(event) => setRejectLabel(event.target.value)}
                />
              </div>
              {mode === 'hosted' ? (
                <HostedOfferPreview
                  title={title}
                  subtext={subtext}
                  mediaUrl={mediaUrl}
                  acceptLabel={acceptLabel}
                  rejectLabel={rejectLabel}
                  fullPrice={selectedOption?.price ?? selectedProduct.price}
                  nowPrice={money(priceAmount, selectedCurrency)}
                  plan={selectedPlan}
                  showFrom={clampNumber(parseDecimal(discount), 0, 100) > 0}
                />
              ) : null}
              {selectedPlan ? (
                <>
                  <div className={modalDivider} />
                  <div className={funnelHint}>
                    <RefreshCw size={15} />
                    <span>
                      {selectedProduct.name} é uma assinatura. Ao aceitar, o comprador vira
                      assinante do plano {selectedPlan.name} ({planPriceLabel(selectedPlan)}).
                    </span>
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>

        <footer className={funnelStageFooter}>
          <div>
            {editing ? (
              <Button
                variant="text"
                leftIcon={<Trash2 size={16} />}
                onClick={() => onRemove(state)}
              >
                Remover etapa
              </Button>
            ) : null}
          </div>
          <div className={actionRow}>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>Salvar etapa</Button>
          </div>
        </footer>
      </aside>
    </div>,
    document.body,
  )
}

function renderTracking(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
) {
  function addPixel(provider: Pixel['provider']) {
    const providerPixels = product.pixels.filter((pixel) => pixel.provider === provider)
    if (providerPixels.length >= 10) {
      toast.error({
        title: 'Limite de pixels atingido',
        description: 'Cada provedor pode ter até 10 pixels neste produto.',
      })
      return
    }
    if (providerPixels.some((pixel) => !pixelTrackingReady(pixel))) {
      toast.info({
        title: 'Complete o pixel antes de adicionar outro',
        description: `Preencha o ${pixelProviderMeta[provider].shortTitle} que está incompleto ou remova-o.`,
      })
      return
    }
    patch((current) => ({
      ...current,
      pixels: [...current.pixels, newProductPixel(provider, makeId(provider))],
    }))
  }

  function replacePixel(next: Pixel) {
    patch((current) => ({
      ...current,
      pixels: current.pixels.map((pixel) => (pixel.id === next.id ? next : pixel)),
    }))
  }

  function removePixel(pixelId: string) {
    patch((current) => ({
      ...current,
      pixels: current.pixels.filter((item) => item.id !== pixelId),
    }))
  }

  return (
    <Card className={section}>
      <div className={sectionHeader}>
        <div>
          <h2 className={sectionTitle}>Trackeamento & pixels</h2>
          <p className={sectionCopy}>
            Configure os destinos de tracking salvos no produto. A maioria dos produtos usa um por
            provedor; adicione mais quando precisar duplicar eventos para parceiros ou contas
            separadas.
          </p>
        </div>
      </div>
      <div className={tableLike}>
        {pixelProviders.map((provider) => {
          const meta = pixelProviderMeta[provider]
          const providerPixels = product.pixels.filter((pixel) => pixel.provider === provider)
          const readyCount = providerPixels.filter(pixelTrackingReady).length
          const statusLabel =
            providerPixels.length === 0
              ? 'Não configurado'
              : `${readyCount}/${providerPixels.length} pronto(s)`

          return (
            <div key={provider} className={compactCard}>
              <div className={sectionHeader}>
                <div>
                  <span className={inlineRow}>
                    {meta.icon}
                    <span className={nodeTitle}>{meta.title}</span>
                  </span>
                  <p className={muted}>{meta.description}</p>
                  <p className={muted}>
                    {providerPixels.length > 0
                      ? provider === 'utmify'
                        ? `${providerPixels.length} credencial(is) neste produto.`
                        : `${providerPixels.length} pixel(s) neste produto.`
                      : meta.empty}
                  </p>
                </div>
                <div className={actionRow}>
                  <StatusBadge
                    tone={readyCount > 0 ? 'success' : 'neutral'}
                    label={statusLabel}
                    size="sm"
                  />
                  <Button
                    size="sm"
                    leftIcon={<Plus size={16} />}
                    onClick={() => addPixel(provider)}
                  >
                    Adicionar
                  </Button>
                </div>
              </div>

              <PixelEventsTimeline
                provider={provider}
                googleBeginCheckout={providerPixels.some(
                  (pixel) =>
                    pixel.provider === 'google_ads' && pixel.conversionEvent === 'begin_checkout',
                )}
              />

              {providerPixels.length === 0 ? null : (
                <div className={tableLike}>
                  {providerPixels.map((pixel, index) => {
                    const ready = pixelTrackingReady(pixel)
                    return (
                      <div key={pixel.id} className={compactCard}>
                        <div className={sectionHeader}>
                          <div>
                            <span className={nodeTitle}>
                              {meta.shortTitle} {index + 1}
                            </span>
                            <p className={muted}>{pixelSummary(pixel)}</p>
                          </div>
                          <div className={actionRow}>
                            {pixelServerSideArmed(pixel) ? (
                              <StatusBadge tone="success" label="Server-side armado" size="sm" />
                            ) : null}
                            <StatusBadge
                              tone={ready ? 'success' : 'awaiting'}
                              label={ready ? 'Pronto' : 'Configurar'}
                              size="sm"
                            />
                            <Button
                              variant="secondary"
                              size="sm"
                              aria-label={`Remover ${meta.shortTitle} ${index + 1}`}
                              onClick={() => removePixel(pixel.id)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                        {pixel.provider === 'facebook' || pixel.provider === 'tiktok' ? (
                          <div className={formGrid}>
                            <TextField
                              id={`field-pixels.${pixel.id}.pixelId`}
                              label="Pixel ID"
                              value={pixel.pixelId}
                              placeholder={pixel.provider === 'tiktok' ? 'C12345...' : '1234567890'}
                              error={
                                fieldErrors[`pixels.${pixel.id}.pixelId`] ??
                                pixelIdFormatError(pixel)
                              }
                              onChange={(event) => {
                                clearFieldErrors(`pixels.${pixel.id}.pixelId`)
                                replacePixel({ ...pixel, pixelId: event.target.value.trim() })
                              }}
                            />
                            <TextField
                              label="Access token"
                              type="password"
                              autoComplete="new-password"
                              value={pixel.accessToken}
                              placeholder="Opcional: arma a trilha server-side"
                              helperText="Opcional. Com token, Purchase e AbandonedCart saem pelo servidor."
                              onChange={(event) =>
                                replacePixel({ ...pixel, accessToken: event.target.value.trim() })
                              }
                            />
                          </div>
                        ) : pixel.provider === 'google_ads' ? (
                          <div className={formGrid}>
                            <TextField
                              id={`field-pixels.${pixel.id}.pixelId`}
                              label="Conversion ID"
                              value={pixel.pixelId}
                              placeholder="AW-123456789"
                              error={
                                fieldErrors[`pixels.${pixel.id}.pixelId`] ??
                                pixelIdFormatError(pixel)
                              }
                              onChange={(event) => {
                                clearFieldErrors(`pixels.${pixel.id}.pixelId`)
                                replacePixel({ ...pixel, pixelId: event.target.value.trim() })
                              }}
                            />
                            <TextField
                              id={`field-pixels.${pixel.id}.conversionLabel`}
                              label="Rótulo de conversão"
                              value={pixel.conversionLabel}
                              placeholder="abcDEF123..."
                              error={fieldErrors[`pixels.${pixel.id}.conversionLabel`]}
                              onChange={(event) => {
                                clearFieldErrors(`pixels.${pixel.id}.conversionLabel`)
                                replacePixel({
                                  ...pixel,
                                  conversionLabel: event.target.value.trim(),
                                })
                              }}
                            />
                            <SelectField
                              label="Evento de conversão"
                              value={pixel.conversionEvent}
                              options={googleConversionEventOptions}
                              onChange={(event) =>
                                replacePixel({
                                  ...pixel,
                                  conversionEvent:
                                    event.target.value === 'begin_checkout'
                                      ? 'begin_checkout'
                                      : 'purchase',
                                })
                              }
                            />
                          </div>
                        ) : (
                          <div className={formGrid}>
                            <TextField
                              id={`field-pixels.${pixel.id}.accessToken`}
                              label="Credencial de API"
                              type="password"
                              autoComplete="new-password"
                              value={pixel.accessToken}
                              placeholder="x-api-token do painel da UTMify"
                              error={fieldErrors[`pixels.${pixel.id}.accessToken`]}
                              onChange={(event) => {
                                clearFieldErrors(`pixels.${pixel.id}.accessToken`)
                                replacePixel({ ...pixel, accessToken: event.target.value.trim() })
                              }}
                            />
                          </div>
                        )}
                        <div className={segmentedField}>
                          <span className={segmentedLabel}>Enviar valor</span>
                          {pixel.sendOnlyCommissionLocked ? (
                            <span className={couponMetricValue}>
                              Comissão · definida pelo afiliado
                            </span>
                          ) : (
                            <SegmentedControl<'commission' | 'gross'>
                              ariaLabel={`${meta.shortTitle} enviar valor`}
                              value={pixel.sendOnlyCommission ? 'commission' : 'gross'}
                              options={[
                                { value: 'commission', label: 'Comissão' },
                                { value: 'gross', label: 'Bruto' },
                              ]}
                              onChange={(value) =>
                                replacePixel({
                                  ...pixel,
                                  sendOnlyCommission: value === 'commission',
                                })
                              }
                            />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function FunnelCommissionItems({
  product,
  patch,
}: {
  product: Product
  patch: (updater: (current: Product) => Product) => void
}) {
  const productsResult = useProductEditorProductsQuery()
  const offerProducts = useMemo(
    () => getOfferProductOptions(product, productsResult.data, funnelSelectionsOf(product)),
    [product, productsResult.data],
  )
  const masterOn = product.affiliate.commissionsFunnel

  const items = useMemo(() => {
    const resolve = (offerId: string, planId: string | null) =>
      findOptionForSelection(offerProducts, offerId, planId)
    const targetLabel = (option: OfferProductOption | undefined) => {
      if (!option) return 'Produto da oferta'
      return option.plan ? `${option.product.name} · ${option.plan.name}` : option.label
    }
    const bumps = product.orderBumps.map((bump) => {
      const target = resolve(bump.targetOfferId, bump.planId)
      return {
        id: bump.id,
        kind: 'order_bump' as const,
        stageLabel: 'Order bump',
        name: bump.title,
        targetName: targetLabel(target),
        targetEnables: target?.product.affiliate.enabled ?? false,
        commissionable: bump.commissionable,
      }
    })
    const nodes = product.funnelNodes.map((node) => {
      const target = resolve(node.offerId, node.planId)
      return {
        id: node.id,
        kind: node.kind,
        stageLabel: node.kind === 'downsell' ? 'Downsell' : 'Upsell',
        name: node.title,
        targetName: targetLabel(target),
        targetEnables: target?.product.affiliate.enabled ?? false,
        commissionable: node.commissionable,
      }
    })
    return [...bumps, ...nodes]
  }, [product.orderBumps, product.funnelNodes, offerProducts])

  if (items.length === 0) {
    return null
  }

  function setCommission(kind: 'order_bump' | FunnelNode['kind'], id: string, checked: boolean) {
    patch((current) =>
      kind === 'order_bump'
        ? {
            ...current,
            orderBumps: current.orderBumps.map((bump) =>
              bump.id === id ? { ...bump, commissionable: checked } : bump,
            ),
          }
        : {
            ...current,
            funnelNodes: current.funnelNodes.map((node) =>
              node.id === id ? { ...node, commissionable: checked } : node,
            ),
          },
    )
  }

  return (
    <div className={compactCard}>
      <div className={inlineHeaderRow}>
        <div>
          <span className={inlineRow}>
            <GitFork size={16} />
            <span className={featureTitle}>Comissão de afiliado no funil</span>
          </span>
          <p className={muted}>
            {masterOn
              ? 'Ligado, o afiliado da venda original também ganha nas etapas marcadas abaixo. Etapas cujo produto-alvo não aceita afiliados ficam bloqueadas: habilite afiliados naquele produto para liberar.'
              : 'Desligado, só o produto principal comissiona o afiliado. Ligue para comissionar etapa a etapa.'}
          </p>
        </div>
        <Toggle
          checked={masterOn}
          onChange={(checked) =>
            patch((current) => ({
              ...current,
              affiliate: { ...current.affiliate, commissionsFunnel: checked },
            }))
          }
          ariaLabel="Afiliado comissiona em todo o funil"
        />
      </div>
      <div className={tableLike}>
        {items.map((item) => {
          const blocked = !item.targetEnables
          return (
            <div key={item.id} className={rowCard}>
              <div>
                <span className={nodeTitle}>{item.name}</span>
                <p className={muted}>
                  {item.stageLabel} · {item.targetName}
                </p>
                {blocked ? (
                  <span className={inlineRow}>
                    <TriangleAlert size={13} />
                    <span className={muted}>
                      Habilite afiliados em “{item.targetName}” para liberar esta etapa.
                    </span>
                  </span>
                ) : null}
              </div>
              <Toggle
                checked={masterOn && item.targetEnables && item.commissionable}
                disabled={!masterOn || blocked}
                onChange={(value) => setCommission(item.kind, item.id, value)}
                ariaLabel={`Comissão de afiliado na etapa ${item.name}`}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function renderAffiliates(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
) {
  const affiliatesEnabled = product.affiliate.enabled
  const inviteLink = getAffiliateInviteLink(product)

  return (
    <Card className={section}>
      <div className={sectionHeader}>
        <div>
          <h2 className={sectionTitle}>Afiliados</h2>
          <p className={sectionCopy}>
            Defina se este produto aceita afiliados e quais regras entram no split.
          </p>
        </div>
        <div className={inlineRow}>
          <StatusBadge
            tone={affiliatesEnabled ? 'success' : 'neutral'}
            size="sm"
            label={affiliatesEnabled ? 'Aceita afiliados' : 'Não aceita afiliados'}
          />
          <Toggle
            checked={affiliatesEnabled}
            onChange={(checked) =>
              patch((current) => ({
                ...current,
                affiliate: { ...current.affiliate, enabled: checked },
              }))
            }
            ariaLabel="Aceitar afiliados neste produto"
          />
        </div>
      </div>

      <div className={compactCard}>
        <span className={inlineRow}>
          <Users size={16} />
          <span className={featureTitle}>
            {affiliatesEnabled
              ? 'Este produto pode receber vendas de afiliados'
              : 'Este produto não recebe vendas de afiliados'}
          </span>
        </span>
        <p className={muted}>
          {affiliatesEnabled
            ? 'Afiliados aprovados podem vender este produto. Em ofertas pós-compra, o controle do funil apenas decide se o afiliado da venda original também entra naquela oferta.'
            : 'Links de afiliado não geram comissão para este produto. Ofertas de funil também ficam sem split de afiliado enquanto esta opção estiver desligada.'}
        </p>
      </div>

      {affiliatesEnabled ? (
        <>
          <div className={formGrid}>
            <PercentageField
              label="Comissão padrão do afiliado"
              helperText="Entre 5% e 90%. Usada na venda principal e nas ofertas que incluírem afiliado no split."
              value={product.affiliate.commission}
              min={5}
              max={90}
              onValueChange={(commission) =>
                patch((current) => ({
                  ...current,
                  affiliate: {
                    ...current.affiliate,
                    commission,
                  },
                }))
              }
              onValueCommit={(commission) =>
                patch((current) => ({
                  ...current,
                  affiliate: {
                    ...current.affiliate,
                    commission,
                  },
                }))
              }
            />
            <div className={compactCard}>
              <span className={inlineRow}>
                <LinkIcon size={16} />
                <span className={featureTitle}>Link privado de convite</span>
              </span>
              <p className={muted}>
                {inviteLink || 'Será gerado automaticamente ao salvar este produto.'}
              </p>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Copy size={16} />}
                disabled={!inviteLink}
                onClick={() => {
                  void navigator.clipboard?.writeText(inviteLink)
                  toast.success('Link de convite copiado.')
                }}
              >
                Copiar link
              </Button>
            </div>
          </div>
          {product.affiliate.manualApproval ? (
            <Banner tone="info" title="Afiliados entram como pendentes">
              Novas solicitações precisam ser aprovadas antes de gerar links com comissão.
            </Banner>
          ) : null}
          <div className={featureGrid}>
            {affiliateToggleOptions.map(({ key, label, description, icon }) => (
              <div key={key} className={featureCard}>
                <div>
                  <span className={inlineRow}>
                    {icon}
                    <span className={featureTitle}>{label}</span>
                  </span>
                  <p className={muted}>{description}</p>
                </div>
                <Toggle
                  checked={product.affiliate[key]}
                  onChange={(checked) =>
                    patch((current) => ({
                      ...current,
                      affiliate: { ...current.affiliate, [key]: checked },
                    }))
                  }
                  ariaLabel={label}
                />
              </div>
            ))}
          </div>
          <FunnelCommissionItems product={product} patch={patch} />
        </>
      ) : null}
    </Card>
  )
}

function getAffiliateInviteLink(product: Product) {
  const token =
    product.affiliate.inviteToken.trim() ||
    affiliateInviteTokenFromUrl(product.links.affiliateInvite)
  return token ? affiliateInviteUrl(encodeURIComponent(token)) : ''
}

function renderCoupons(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
) {
  function addCoupon(preset: (typeof couponPresetOptions)[number] = defaultCouponPreset) {
    patch((current) => ({
      ...current,
      coupons: (() => {
        const { startsAt, endsAt } = couponPresetDates(preset.durationDays)
        const existingCodes = new Set(current.coupons.map((coupon) => coupon.code))
        const baseCode = sanitizeCouponCode(preset.code)
        const code = existingCodes.has(baseCode)
          ? sanitizeCouponCode(`${baseCode}${current.coupons.length + 1}`)
          : baseCode

        return [
          ...current.coupons,
          {
            id: makeId('coupon'),
            code,
            type: preset.type,
            percent: preset.type === 'percent' ? preset.percent : 0,
            amount: money(preset.amount, current.price.currency),
            startsAt,
            endsAt,
            usageLimit: preset.usageLimit,
            active: true,
          },
        ]
      })(),
    }))
  }

  function updateCoupon(couponId: string, input: Partial<Coupon>) {
    patch((current) => ({
      ...current,
      coupons: current.coupons.map((coupon) =>
        coupon.id === couponId ? { ...coupon, ...input } : coupon,
      ),
    }))
  }

  function updateCouponType(coupon: Coupon, type: Coupon['type']) {
    updateCoupon(coupon.id, {
      type,
      percent: type === 'percent' ? Math.max(1, coupon.percent || 10) : 0,
      amount:
        type === 'fixed'
          ? money(
              isPositiveAmount(coupon.amount.amount)
                ? couponAmountForCurrency(coupon, product.price.currency).amount
                : '50.000',
              product.price.currency,
            )
          : coupon.amount,
    })
  }

  function updateCouponWindow(coupon: Coupon, input: Partial<Pick<Coupon, 'startsAt' | 'endsAt'>>) {
    const currentWindow = couponDateWindow(coupon)
    const startsAt =
      input.startsAt !== undefined
        ? normalizeCouponDateInput(input.startsAt)
        : currentWindow.startsAt
    const endsAt =
      input.endsAt !== undefined ? normalizeCouponDateInput(input.endsAt) : currentWindow.endsAt
    updateCoupon(coupon.id, {
      startsAt,
      endsAt: startsAt && endsAt && endsAt < startsAt ? startsAt : endsAt,
    })
  }

  function toggleCouponWindow(coupon: Coupon, enabled: boolean) {
    updateCoupon(coupon.id, enabled ? defaultCouponDates() : { startsAt: '', endsAt: '' })
  }

  function removeCoupon(couponId: string) {
    patch((current) => ({
      ...current,
      coupons: current.coupons.filter((item) => item.id !== couponId),
    }))
  }

  return (
    <Card className={section}>
      <div className={sectionHeader}>
        <div>
          <h2 className={sectionTitle}>Cupons</h2>
          <p className={sectionCopy}>
            Códigos com validade opcional, limite de uso e desconto aplicado antes da projeção do
            ledger.
          </p>
        </div>
        <Button leftIcon={<TicketPercent size={16} />} onClick={() => addCoupon()}>
          Novo cupom
        </Button>
      </div>

      <CouponScopeNotice />

      <div className={couponPresetGrid}>
        {couponPresetOptions.map((preset) => (
          <button
            key={preset.code}
            type="button"
            className={couponPresetCard()}
            onClick={() => addCoupon(preset)}
          >
            <span className={billingModelIcon}>{preset.icon}</span>
            <span>
              <span className={billingModelTitle}>{preset.label}</span>
              <span className={billingModelCopy}>{preset.description}</span>
              <span className={billingModelDetail}>
                {couponPresetDetail(preset, product.price.currency)}
              </span>
            </span>
          </button>
        ))}
      </div>

      {product.coupons.length === 0 ? (
        <div className={compactCard}>
          <span className={nodeTitle}>Nenhum cupom criado</span>
          <p className={muted}>
            Use um preset ou crie um cupom manual para liberar o campo de desconto no checkout.
          </p>
        </div>
      ) : null}

      {product.coupons.map((coupon) => {
        const status = couponStatus(coupon)
        const preview = couponPricePreview(product, coupon)
        const window = couponDateWindow(coupon)
        const codeLabel = coupon.code || 'NOVOCUPOM'
        const hasDateWindow = Boolean(window.startsAt || window.endsAt)
        const today = isoDateFromLocal(new Date())
        const minimumEndDate = window.startsAt && window.startsAt > today ? window.startsAt : today

        return (
          <div key={coupon.id} className={couponCard}>
            <div className={editorStack}>
              <div className={sectionHeader}>
                <div className={couponCodeBlock}>
                  <span className={couponMetricLabel}>
                    <TicketPercent size={14} />
                    Código do cupom
                  </span>
                  <span className={couponCodeValue}>{codeLabel}</span>
                  <span className={muted}>Não acumula com outros cupons.</span>
                </div>
                <div className={actionRow}>
                  <span className={couponStatusPill({ tone: status.tone })}>{status.label}</span>
                  <Toggle
                    checked={coupon.active}
                    onChange={(active) => updateCoupon(coupon.id, { active })}
                    ariaLabel={`Status do cupom ${codeLabel}`}
                  />
                </div>
              </div>

              <div className={couponSummaryGrid}>
                <div className={couponMetric}>
                  <span className={couponMetricLabel}>
                    <Tag size={14} />
                    Desconto
                  </span>
                  <span className={couponMetricValue}>
                    {couponDiscountLabel(coupon, product.price.currency)}
                  </span>
                </div>
                <div className={couponMetric}>
                  <span className={couponMetricLabel}>
                    <CalendarDays size={14} />
                    Validade
                  </span>
                  <span className={couponMetricValue}>{couponWindowLabel(coupon)}</span>
                </div>
                <div className={couponMetric}>
                  <span className={couponMetricLabel}>
                    <ShieldCheck size={14} />
                    Uso
                  </span>
                  <span className={couponMetricValue}>Até {coupon.usageLimit} usos</span>
                </div>
                <div className={couponMetric}>
                  <span className={couponMetricLabel}>
                    <TrendingDown size={14} />
                    Total estimado
                  </span>
                  <span className={couponMetricValue}>{preview.total}</span>
                </div>
              </div>

              <div className={formGrid}>
                <TextField
                  label="Código"
                  value={coupon.code}
                  maxLength={25}
                  helperText="A-Z e 0-9, até 25 caracteres."
                  error={fieldErrors[`coupons.${coupon.id}.code`]}
                  onChange={(event) => {
                    clearFieldErrors(`coupons.${coupon.id}.code`)
                    updateCoupon(coupon.id, {
                      code: sanitizeCouponCode(event.target.value),
                    })
                  }}
                />
                <div className={segmentedField}>
                  <span className={segmentedLabel}>Tipo de desconto</span>
                  <SegmentedControl
                    value={coupon.type}
                    options={[
                      { label: 'Percentual', value: 'percent' },
                      { label: 'Valor fixo', value: 'fixed' },
                    ]}
                    onChange={(value) => updateCouponType(coupon, value)}
                    ariaLabel={`Tipo de desconto do cupom ${codeLabel}`}
                  />
                </div>
                {coupon.type === 'percent' ? (
                  <PercentageField
                    label="Desconto percentual"
                    min={1}
                    max={95}
                    value={coupon.percent}
                    onValueChange={(percent) =>
                      updateCoupon(coupon.id, {
                        percent: parsePercentage(percent),
                      })
                    }
                    onValueCommit={(percent) =>
                      updateCoupon(coupon.id, {
                        percent: parsePercentage(percent),
                      })
                    }
                  />
                ) : (
                  <CurrencyField
                    label="Valor fixo"
                    currency={product.price.currency}
                    value={coupon.amount.amount}
                    error={fieldErrors[`coupons.${coupon.id}.amount.amount`]}
                    onValueChange={(amount) => {
                      clearFieldErrors(`coupons.${coupon.id}.amount.amount`)
                      updateCoupon(coupon.id, {
                        amount: money(amount, product.price.currency),
                      })
                    }}
                  />
                )}
              </div>

              <div className={featureCard}>
                <div>
                  <span className={featureTitle}>Definir validade</span>
                  <p className={muted}>
                    {hasDateWindow
                      ? couponWindowLabel(coupon)
                      : 'Sem prazo: o cupom fica ativo enquanto o status estiver ligado e houver usos disponíveis.'}
                  </p>
                </div>
                <Toggle
                  checked={hasDateWindow}
                  onChange={(enabled) => toggleCouponWindow(coupon, enabled)}
                  ariaLabel={`Definir validade do cupom ${codeLabel}`}
                />
              </div>

              <div className={formGrid}>
                {hasDateWindow ? (
                  <>
                    <DateField
                      label="Início da validade"
                      placeholder="Sem início"
                      value={window.startsAt}
                      onChange={(event) =>
                        updateCouponWindow(coupon, { startsAt: event.target.value })
                      }
                    />
                    <DateField
                      label="Fim da validade"
                      placeholder="Sem fim"
                      value={window.endsAt}
                      min={minimumEndDate}
                      error={fieldErrors[`coupons.${coupon.id}.endsAt`]}
                      onChange={(event) => {
                        clearFieldErrors(`coupons.${coupon.id}.endsAt`)
                        updateCouponWindow(coupon, { endsAt: event.target.value })
                      }}
                    />
                  </>
                ) : null}
                <TextField
                  label="Limite de usos"
                  type="number"
                  min={1}
                  max={999999}
                  value={coupon.usageLimit}
                  onChange={(event) =>
                    updateCoupon(coupon.id, {
                      usageLimit: clampIntegerInput(event.target.value, 1, 999999),
                    })
                  }
                />
              </div>

              <div className={couponPreview}>
                <div className={couponPreviewRow}>
                  <span>Oferta principal</span>
                  <strong>{formatBuyerPrice(product.price)}</strong>
                </div>
                <div className={couponPreviewRow}>
                  <span>Desconto aplicado</span>
                  <strong>{preview.discount}</strong>
                </div>
                <div className={couponPreviewTotal}>
                  <span>Total estimado</span>
                  <span>{preview.total}</span>
                </div>
                {preview.note ? <p className={muted}>{preview.note}</p> : null}
              </div>
            </div>
            <Button variant="secondary" onClick={() => removeCoupon(coupon.id)}>
              <Trash2 size={16} />
            </Button>
          </div>
        )
      })}
    </Card>
  )
}

const coproductionStatusTones = {
  PENDENTE: 'awaiting',
  ATIVO: 'success',
  REMOVIDO: 'closed',
} satisfies Record<CoproductionStatus, StatusTone>

function coproductionStatusTone(status: CoproductionStatus): StatusTone {
  return coproductionStatusTones[status]
}

function formatCoproductionDateTime(value: string | undefined) {
  if (!value) return 'Não informado'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Não informado'

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function coproductionInviteDescription(
  coproducer: Product['coproducers'][number],
  invite: CoproductionInvite | null | undefined,
) {
  if (isLocalCoproducerId(coproducer.id)) {
    return 'Ainda não enviado. Salve a seção para disparar o convite ao coprodutor.'
  }

  if (coproducer.status === 'PENDENTE') {
    return invite?.expiresAt
      ? 'Convite enviado e aguardando aceite do coprodutor.'
      : 'Convite enviado. Aguarde o aceite do coprodutor.'
  }

  if (coproducer.status === 'ATIVO') {
    return 'Convite aceito. O coprodutor entra nos próximos splits deste produto.'
  }

  return 'Convite removido. O coprodutor não participa dos próximos splits.'
}

function coproductionStatusPresentation(coproducer: Product['coproducers'][number]) {
  if (isLocalCoproducerId(coproducer.id)) {
    return { label: 'Ainda não enviado', tone: 'informative' as const }
  }

  return {
    label: coproductionStatusLabel(coproducer.status),
    tone: coproductionStatusTone(coproducer.status),
  }
}

function renderCoproduction(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
  coproductionInvite: CoproductionInvite | null | undefined,
  feePreviewState: FeePreviewState,
) {
  const coproducer = product.coproducers[0]
  const coproducerPercentage = parseDecimal(coproducer?.percentage ?? '0')
  const splitPreview = feePreviewState.data
    ? computeSplitPreview({
        amount: product.price,
        vendepayFee: feePreviewState.data.totalFee,
        coProducerPercentage: coproducer?.percentage ?? '0',
      })
    : undefined
  const status = coproducer ? coproductionStatusPresentation(coproducer) : null
  const hasPersistedCoproducer = coproducer ? !isLocalCoproducerId(coproducer.id) : false
  const canEditPercentage = !hasPersistedCoproducer || coproducer?.status === 'ATIVO'
  return (
    <Card className={section}>
      <div>
        <h2 className={sectionTitle}>Co-produção</h2>
        <p className={sectionCopy}>
          Começa com 1 coprodutor para manter o contrato financeiro. Produtor fica com o resíduo.
        </p>
      </div>
      {!hasPersistedCoproducer || canEditPercentage ? (
        <div className={formGrid}>
          {!hasPersistedCoproducer ? (
            <TextField
              label="E-mail do coprodutor"
              value={coproducer?.email ?? ''}
              error={coproducer?.id ? fieldErrors[`coproducers.${coproducer.id}.email`] : undefined}
              onChange={(event) => {
                if (coproducer?.id) clearFieldErrors(`coproducers.${coproducer.id}.email`)
                patch((current) => ({
                  ...current,
                  coproducers: [
                    {
                      id: coproducer?.id ?? makeId('copro'),
                      email: event.target.value,
                      percentage: coproducer?.percentage ?? '10.000',
                      status: coproducer?.status ?? 'PENDENTE',
                    },
                  ],
                }))
              }}
            />
          ) : null}
          {canEditPercentage ? (
            <PercentageField
              label={hasPersistedCoproducer ? 'Percentual do coprodutor' : 'Percentual'}
              value={coproducer?.percentage ?? '10.000'}
              min={1}
              max={99}
              onValueChange={(percentage) =>
                patch((current) => ({
                  ...current,
                  coproducers: [
                    {
                      id: coproducer?.id ?? makeId('copro'),
                      email: coproducer?.email ?? '',
                      percentage,
                      status: coproducer?.status ?? 'PENDENTE',
                    },
                  ],
                }))
              }
              onValueCommit={(percentage) =>
                patch((current) => ({
                  ...current,
                  coproducers: [
                    {
                      id: coproducer?.id ?? makeId('copro'),
                      email: coproducer?.email ?? '',
                      percentage,
                      status: coproducer?.status ?? 'PENDENTE',
                    },
                  ],
                }))
              }
            />
          ) : null}
        </div>
      ) : null}
      {coproducer ? (
        <div className={compactCard}>
          <div className={sectionHeader}>
            <div>
              <span className={inlineRow}>
                <Handshake size={16} />
                <span className={featureTitle}>Status do convite</span>
              </span>
              <p className={muted}>
                {coproductionInviteDescription(coproducer, coproductionInvite)}
              </p>
            </div>
            {status ? <StatusBadge tone={status.tone} label={status.label} size="sm" /> : null}
          </div>
          <div className={checkoutSummaryGrid}>
            <div className={checkoutSummaryItem}>
              <span className={checkoutSummaryLabel}>Enviado para</span>
              <span className={checkoutSummaryValue}>
                {coproducer.email.trim() || 'Informe o e-mail'}
              </span>
            </div>
            <div className={checkoutSummaryItem}>
              <span className={checkoutSummaryLabel}>Participação</span>
              <span className={checkoutSummaryValue}>
                {splitPreview
                  ? `Coprodutor ${formatPercentageLabel(coproducerPercentage)} (${formatMoneyValue(splitPreview.coProducer)}) · taxa ${formatMoneyValue(splitPreview.vendepayFee)} · produtor ${formatPercentageLabel(100 - coproducerPercentage)} (${formatMoneyValue(splitPreview.producer)})`
                  : 'Aguardando o cálculo da taxa efetiva.'}
              </span>
            </div>
            <div className={checkoutSummaryItem}>
              <span className={checkoutSummaryLabel}>Enviado em</span>
              <span className={checkoutSummaryValue}>
                {isLocalCoproducerId(coproducer.id)
                  ? 'Aguardando salvar'
                  : formatCoproductionDateTime(coproductionInvite?.createdAt)}
              </span>
            </div>
            {coproductionInvite?.acceptedAt ? (
              <div className={checkoutSummaryItem}>
                <span className={checkoutSummaryLabel}>Aceito em</span>
                <span className={checkoutSummaryValue}>
                  {formatCoproductionDateTime(coproductionInvite.acceptedAt)}
                </span>
              </div>
            ) : null}
            {coproductionInvite?.expiresAt && !coproductionInvite.acceptedAt ? (
              <div className={checkoutSummaryItem}>
                <span className={checkoutSummaryLabel}>Expira em</span>
                <span className={checkoutSummaryValue}>
                  {formatCoproductionDateTime(coproductionInvite.expiresAt)}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {splitPreview ? (
        <Banner tone="info" title="Split previsto">
          Produtor: {formatMoneyValue(splitPreview.producer)}. Coprodutor:{' '}
          {formatMoneyValue(splitPreview.coProducer)}. Taxa Vendepay estimada:{' '}
          {formatMoneyValue(splitPreview.vendepayFee)}.
        </Banner>
      ) : (
        <FeePolicyPreview state={feePreviewState} />
      )}
    </Card>
  )
}

const emailThemePresets = [
  { color: '#049366', tone: 'green' },
  { color: '#045471', tone: 'teal' },
  { color: '#2A6FDB', tone: 'blue' },
  { color: '#7A3BFF', tone: 'violet' },
  { color: '#111827', tone: 'graphite' },
] as const

function EmailStep({
  product,
  patch,
  fieldErrors,
  clearFieldErrors,
}: {
  product: Product
  patch: (updater: (current: Product) => Product) => void
  fieldErrors: ProductEditorFieldErrors
  clearFieldErrors: (...paths: string[]) => void
}) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
  const messageRef = useRef<HTMLTextAreaElement>(null)
  const template = product.emailTemplate
  const brandColor = template.brandColor || '#049366'
  const logoUrl = template.logoUrl || ''

  const context = useMemo<EmailPreviewContext>(
    () => ({
      productName: product.name.trim() || 'seu produto',
      supportEmail: product.supportEmail.trim() || 'suporte@vendepay.com',
      amountLabel: formatMoneyValue(product.price),
    }),
    [product.name, product.price, product.supportEmail],
  )
  const preview = useMemo(() => projectEmailPreview(template, context), [template, context])
  const sellerPreviewName = product.supportName.trim() || product.name.trim() || 'VendePay'

  const update = (part: Partial<Product['emailTemplate']>) =>
    patch((current) => ({
      ...current,
      emailTemplate: { ...current.emailTemplate, ...part },
    }))

  const insertVariable = (token: string) => {
    const element = messageRef.current
    const value = template.html
    if (!element) {
      const separator = value.length === 0 || value.endsWith(' ') ? '' : ' '
      update({ html: `${value}${separator}${token}` })
      return
    }
    const start = element.selectionStart ?? value.length
    const end = element.selectionEnd ?? value.length
    update({ html: `${value.slice(0, start)}${token}${value.slice(end)}` })
    const caret = start + token.length
    requestAnimationFrame(() => {
      element.focus()
      element.setSelectionRange(caret, caret)
    })
  }

  return (
    <Card className={section}>
      <div className={sectionHeader}>
        <div>
          <h2 className={sectionTitle}>E-mail de compra</h2>
          <p className={sectionCopy}>
            Escreva a mensagem. A VendePay aplica o cabeçalho e o rodapé com o visual premium
            automaticamente.
          </p>
        </div>
        <Toggle
          checked={template.active}
          onChange={(checked) => update({ active: checked })}
          ariaLabel="Ativar e-mail de compra"
        />
      </div>

      <TextField
        label="Assunto"
        value={template.subject}
        onChange={(event) => update({ subject: event.target.value })}
      />

      <div className={emailThemeControls}>
        <div>
          <label className={emailFieldLabel}>Cor</label>
          <div className={emailThemeSwatchGrid}>
            {emailThemePresets.map(({ color, tone }) => (
              <button
                key={color}
                type="button"
                className={emailThemeSwatch({ selected: brandColor.toUpperCase() === color, tone })}
                aria-label={`Usar cor ${color}`}
                onClick={() => update({ brandColor: color })}
              />
            ))}
          </div>
        </div>
        <TextField
          label="Cor custom"
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : '#049366'}
          error={fieldErrors['emailTemplate.brandColor']}
          onChange={(event) => {
            clearFieldErrors('emailTemplate.brandColor')
            update({ brandColor: event.target.value })
          }}
        />
      </div>

      <ImageUploadField
        label="Logo"
        kind="logo"
        value={logoUrl}
        error={fieldErrors['emailTemplate.logoUrl']}
        onChange={(logoUrl) => {
          clearFieldErrors('emailTemplate.logoUrl')
          update({ logoUrl })
        }}
        aspect="10 / 3"
        hint="PNG ~400x120"
        allowUrl
      />

      <div>
        <label className={emailFieldLabel} htmlFor="email-message">
          Mensagem
        </label>
        <textarea
          id="email-message"
          ref={messageRef}
          className={emailMessageInput}
          value={template.html}
          onChange={(event) => update({ html: event.target.value })}
          placeholder="Obrigado pela compra! Seu acesso já está liberado."
        />
        <span className={emailVarHint}>Até 6 parágrafos são enviados no e-mail.</span>
      </div>

      <div className={inlineRow}>
        <span className={emailVarHint}>Inserir variável:</span>
        {emailVariables.map((variable) => (
          <Button
            key={variable.token}
            variant="secondary"
            leftIcon={<Mail size={15} />}
            onClick={() => insertVariable(variable.token)}
          >
            {variable.token}
          </Button>
        ))}
      </div>

      <div className={emailPreviewHeader}>
        <span className={emailPreviewHeading}>
          <Eye size={15} />
          Pré-visualização
        </span>
        <SegmentedControl
          value={device}
          onChange={(value) => setDevice(value)}
          ariaLabel="Dispositivo da pré-visualização"
          options={[
            { value: 'desktop', label: 'Desktop' },
            { value: 'mobile', label: 'Mobile' },
          ]}
        />
      </div>

      {template.active ? null : (
        <span className={emailInactiveNote}>
          <TriangleAlert size={15} />
          E-mail desativado: os compradores não vão receber esta mensagem.
        </span>
      )}

      <div className={emailPreviewViewport}>
        <div className={emailDoc({ device })}>
          <div className={emailDocHeader}>
            {logoUrl ? (
              <img src={logoUrl} alt={sellerPreviewName} className={emailThemePreviewLogo} />
            ) : (
              <span className={emailDocSubject}>{sellerPreviewName}</span>
            )}
          </div>
          <div className={emailDocBody}>
            <div className={emailDocCard} style={{ borderTopColor: brandColor }}>
              <p className={emailDocSubject}>{preview.title}</p>
              {preview.paragraphs.map((paragraph, index) => (
                <p key={`${index}-${paragraph.slice(0, 12)}`} className={emailDocText}>
                  {paragraph}
                </p>
              ))}
              <div className={emailDocFacts}>
                {preview.facts.map((fact) => (
                  <div key={fact.label} className={emailDocFactRow}>
                    <span className={emailDocFactLabel} style={{ color: brandColor }}>
                      {fact.label}
                    </span>
                    <span className={emailDocFactValue}>{fact.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={emailDocFooter}>
              <p className={emailDocFooterText}>
                Precisa de ajuda? Fale com{' '}
                <span className={emailDocFooterStrong} style={{ color: brandColor }}>
                  {preview.supportEmail}
                </span>
              </p>
              <p className={emailDocFooterText}>{preview.footerNote}</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

function renderRecurrencePlans(
  product: Product,
  patch: (updater: (current: Product) => Product) => void,
  fieldErrors: ProductEditorFieldErrors,
  clearFieldErrors: (...paths: string[]) => void,
  minuteBillingEnabled: boolean,
) {
  function addPlan(preset: (typeof recurrencePresetOptions)[number] = defaultRecurrencePreset) {
    let newPlanId = ''
    patch((current) => {
      const newPlan = createRecurrencePlan(current, preset)
      newPlanId = newPlan.id
      return {
        ...current,
        recurrencePlans: [...current.recurrencePlans, newPlan],
      }
    })
    window.setTimeout(() => {
      document
        .getElementById(`plan-card-${newPlanId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 60)
  }

  function updatePlan(planId: string, input: Partial<RecurrencePlan>) {
    if (input.value !== undefined) {
      const isPrimaryPlan = product.recurrencePlans[0]?.id === planId
      const planIndex = product.recurrencePlans.findIndex((plan) => plan.id === planId)
      const primaryOfferId = primaryOfferIdFor(product)
      clearFieldErrors(
        `recurrencePlans.${planId}.value.amount`,
        ...(planIndex >= 0 ? [`recurrencePlans.${planIndex}.value.amount`] : []),
        ...(isPrimaryPlan
          ? [
              'price.amount',
              'offers.0.price.amount',
              ...(primaryOfferId ? [`offers.${primaryOfferId}.price.amount`] : []),
              'split',
            ]
          : []),
      )
    }
    if (input.trialPrice !== undefined) {
      const planIndex = product.recurrencePlans.findIndex((plan) => plan.id === planId)
      clearFieldErrors(
        `recurrencePlans.${planId}.trialPrice.amount`,
        ...(planIndex >= 0 ? [`recurrencePlans.${planIndex}.trialPrice.amount`] : []),
      )
    }
    patch((current) => {
      const isPrimaryPlan = current.recurrencePlans[0]?.id === planId
      const recurrencePlans = current.recurrencePlans.map((plan) =>
        plan.id === planId ? { ...plan, ...input } : plan,
      )
      const next = { ...current, recurrencePlans }
      return isPrimaryPlan && input.value !== undefined
        ? syncPrimaryOfferPrice(next, input.value)
        : next
    })
  }

  function updatePlanUnit(plan: RecurrencePlan, unit: RecurrencePlan['billingPeriodUnit']) {
    updatePlan(plan.id, {
      billingPeriodUnit: unit,
      billingPeriodCount:
        unit === 'MINUTE' ? Math.min(plan.billingPeriodCount, 10) : plan.billingPeriodCount,
      billingAnchorType: unit === 'MONTH' ? plan.billingAnchorType : 'IMMEDIATE',
      billingAnchorDayOfMonth:
        unit === 'MONTH' && plan.billingAnchorType === 'CALENDAR'
          ? plan.billingAnchorDayOfMonth
          : null,
    })
  }

  function removePlan(planId: string) {
    patch((current) => {
      const removingPrimaryPlan = current.recurrencePlans[0]?.id === planId
      const recurrencePlans = current.recurrencePlans.filter((item) => item.id !== planId)
      const next = { ...current, recurrencePlans }
      return removingPrimaryPlan && recurrencePlans[0] !== undefined
        ? syncPrimaryOfferPrice(next, recurrencePlans[0].value)
        : next
    })
  }

  return (
    <div className={editorStack}>
      <div className={sectionHeader}>
        <div>
          <h3 className={sectionTitle}>
            {MAX_PLANS_PER_PRODUCT > 1 ? 'Planos de assinatura' : 'Plano de assinatura'}
          </h3>
          <p className={sectionCopy}>
            {MAX_PLANS_PER_PRODUCT > 1
              ? 'Um ou mais planos (ex.: Mensal e Anual, ou tiers Básico e Pro). Ajuste tudo na frase de cada plano.'
              : 'Um plano por produto. Para outro ciclo ou tier, crie outro produto.'}
          </p>
        </div>
        {canAddPlan(product) ? (
          <Button leftIcon={<Plus size={16} />} onClick={() => addPlan()}>
            Novo plano
          </Button>
        ) : null}
      </div>

      {product.recurrencePlans.length === 0 ? (
        <div className={compactCard}>
          <span className={nodeTitle}>Nenhum plano criado</span>
          <p className={muted}>
            Crie pelo menos um plano para vender este produto como assinatura.
          </p>
        </div>
      ) : null}

      {editablePlans(product).map((plan, planIndex) => (
        <div key={plan.id} id={`plan-card-${plan.id}`} className={rowCard}>
          <div className={editorStack}>
            <div className={sectionHeader}>
              <div>
                <div className={inlineRow}>
                  <span className={planCardIcon}>
                    <RefreshCw size={16} />
                  </span>
                  <input
                    className={planNameInput}
                    value={plan.name}
                    aria-label="Nome do plano"
                    onChange={(event) => updatePlan(plan.id, { name: event.target.value })}
                  />
                </div>
                <p className={planNameHint}>Identifica este produto na sua tela de Assinaturas.</p>
              </div>
              {MAX_PLANS_PER_PRODUCT > 1 ? (
                <div className={actionRow}>
                  <span className={muted}>{plan.active ? 'Oferta ativa' : 'Oferta inativa'}</span>
                  <Toggle
                    checked={plan.active}
                    onChange={(active) => updatePlan(plan.id, { active })}
                    ariaLabel={`Status do ${plan.name || 'plano'}`}
                  />
                  {planIndex > 0 ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label={`Remover ${plan.name || 'plano'}`}
                      onClick={() => removePlan(plan.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className={sentenceCard}>
              <div className={sentenceText}>
                Cobrar{' '}
                <EditableChip
                  label={formatBuyerPrice(plan.value)}
                  ariaLabel="Editar valor da cobrança"
                >
                  {(close) => (
                    <>
                      <span className={sentencePopoverTitle}>Valor da cobrança</span>
                      <CurrencyField
                        currency={plan.value.currency}
                        value={plan.value.amount}
                        error={fieldErrors[`recurrencePlans.${plan.id}.value.amount`]}
                        onValueChange={(amount) =>
                          updatePlan(plan.id, { value: money(amount, plan.value.currency) })
                        }
                      />
                      <div className={presetRow}>
                        {['27.000', '47.000', '97.000'].map((amount) => (
                          <button
                            key={amount}
                            type="button"
                            className={presetChip}
                            onClick={() =>
                              updatePlan(plan.id, { value: money(amount, plan.value.currency) })
                            }
                          >
                            {formatMoneyValue(money(amount, plan.value.currency))}
                          </button>
                        ))}
                      </div>
                      <div className={popoverNote}>
                        <Globe2 size={13} />
                        <span>Com Mercado Global, o valor por moeda segue o mesmo ciclo.</span>
                      </div>
                      <Button size="sm" onClick={close}>
                        Pronto
                      </Button>
                    </>
                  )}
                </EditableChip>{' '}
                a cada{' '}
                <EditableChip label={String(plan.billingPeriodCount)} ariaLabel="Editar intervalo">
                  {(close) =>
                    renderCadencePopover(
                      plan,
                      updatePlan,
                      updatePlanUnit,
                      minuteBillingEnabled,
                      close,
                    )
                  }
                </EditableChip>{' '}
                <EditableChip
                  label={recurrenceUnitNoun(plan.billingPeriodUnit, plan.billingPeriodCount)}
                  ariaLabel="Editar unidade do ciclo"
                  withChevron
                >
                  {(close) =>
                    renderCadencePopover(
                      plan,
                      updatePlan,
                      updatePlanUnit,
                      minuteBillingEnabled,
                      close,
                    )
                  }
                </EditableChip>
                {plan.billingPeriodUnit === 'MONTH' ? (
                  <>
                    , renovando{' '}
                    <EditableChip
                      label={recurrenceAnchorLabel(plan)}
                      ariaLabel="Editar renovação"
                      withChevron
                    >
                      {(close) => renderAnchorPopover(plan, updatePlan, close)}
                    </EditableChip>
                  </>
                ) : null}
                .
              </div>
              <div className={sentenceResolveRow}>
                <CornerDownRight size={15} />
                <span className={sentenceResolveText}>{recurrenceResolveSentence(plan)}</span>
              </div>
            </div>
            <p className={sentenceHint}>
              Toque em qualquer área <strong>verde</strong> para editar.
            </p>

            <div className={formGrid}>
              <TextField
                label="Descrição do plano (opcional)"
                value={plan.description ?? ''}
                onChange={(event) => updatePlan(plan.id, { description: event.target.value })}
              />
              <div>
                <label className={planFieldLabel} htmlFor={`plan-benefits-${plan.id}`}>
                  Benefícios
                </label>
                <PlanBenefitsInput
                  inputId={`plan-benefits-${plan.id}`}
                  benefits={plan.benefits ?? []}
                  onChange={(next) => updatePlan(plan.id, { benefits: next })}
                />
              </div>
            </div>

            <div className={trialSection}>
              <span className={trialSectionHead}>
                <Gift size={16} /> Período de teste
              </span>
              <div className={trialCardGrid}>
                <button
                  type="button"
                  className={trialCard({ active: !plan.trialEnabled })}
                  onClick={() => updatePlan(plan.id, { trialEnabled: false, trialDays: 0 })}
                >
                  <span className={trialCardTitle}>Sem teste</span>
                  <span className={trialCardCopy}>Cobra o valor cheio já.</span>
                </button>
                <button
                  type="button"
                  className={trialCard({
                    active: plan.trialEnabled && plan.trialType === 'WITH_PAYMENT',
                  })}
                  onClick={() =>
                    updatePlan(plan.id, {
                      trialEnabled: true,
                      trialType: 'WITH_PAYMENT',
                      trialDays: Math.max(1, plan.trialDays || 7),
                    })
                  }
                >
                  <span className={trialCardTitle}>Grátis por dias</span>
                  <span className={trialCardCopy}>Guarda o cartão, cobra depois.</span>
                </button>
                <button
                  type="button"
                  className={trialCard({ active: plan.trialEnabled && plan.trialType === 'PAID' })}
                  onClick={() =>
                    updatePlan(plan.id, {
                      trialEnabled: true,
                      trialType: 'PAID',
                      trialDays: Math.max(1, plan.trialDays || 7),
                      trialPrice: Money.fromMonetaryValue(plan.trialPrice).isZero()
                        ? money(minimumProductBasePriceAmount, plan.value.currency)
                        : plan.trialPrice,
                    })
                  }
                >
                  <span className={trialCardTitle}>Entrada paga</span>
                  <span className={trialCardCopy}>
                    Mínimo de{' '}
                    {formatBuyerPrice(money(minimumProductBasePriceAmount, plan.value.currency))},
                    depois valor cheio.
                  </span>
                </button>
              </div>
              {plan.trialEnabled ? (
                <div className={trialFieldsRow}>
                  <TextField
                    label="Dias de teste"
                    type="number"
                    min={1}
                    max={365}
                    value={plan.trialDays}
                    onChange={(event) =>
                      updatePlan(plan.id, {
                        trialDays: clampIntegerInput(event.target.value, 1, 365),
                      })
                    }
                  />
                  {plan.trialType === 'PAID' ? (
                    <CurrencyField
                      id={`field-recurrencePlans.${plan.id}.trialPrice.amount`}
                      label="Valor da entrada"
                      currency={plan.value.currency}
                      value={plan.trialPrice.amount}
                      error={fieldErrors[`recurrencePlans.${plan.id}.trialPrice.amount`]}
                      onValueChange={(amount) =>
                        updatePlan(plan.id, { trialPrice: money(amount, plan.value.currency) })
                      }
                    />
                  ) : null}
                </div>
              ) : null}
              {renderTrialTimeline(plan)}
            </div>

            <PlanRecoveryAccordion plan={plan} updatePlan={updatePlan} />
          </div>
        </div>
      ))}

      {product.recurrencePlans.length > 0 ? (
        <div>
          <span className={checkoutPreviewLabel}>
            <Eye size={14} /> O comprador escolhe no checkout
          </span>
          <div className={checkoutPreviewList}>
            {product.recurrencePlans
              .filter((plan) => plan.active)
              .map((plan, index) => (
                <div key={plan.id} className={checkoutPreviewRow({ primary: index === 0 })}>
                  <div className={checkoutPreviewMain}>
                    <span className={checkoutPreviewRadio({ selected: index === 0 })} />
                    <div>
                      <div className={checkoutPreviewName}>{plan.name || 'Plano'}</div>
                      {plan.trialEnabled && plan.trialDays > 0 ? (
                        <div className={checkoutPreviewMeta}>{recurrenceTrialLabel(plan)}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className={checkoutPreviewPriceWrap}>
                    <span className={checkoutPreviewPrice}>{formatBuyerPrice(plan.value)}</span>
                    <span className={checkoutPreviewCycle}>{recurrencePeriodSuffix(plan)}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

type PublishControls = {
  onSetPublished: (next: boolean) => void
  isPending: boolean
}

function ProductPublishCard({
  product,
  controls,
}: {
  product: Product
  controls: PublishControls
}) {
  const published = product.status === 'ACTIVE'
  const presentation = getProductStatusPresentation(product.status)
  const StatusIcon = presentation.icon
  return (
    <div className={compactCard}>
      <div className={sectionHeader}>
        <div>
          <span className={nodeTitle}>
            {published ? 'Produto publicado' : 'Produto não publicado'}
          </span>
          <p className={muted}>
            {published
              ? 'Os links abaixo estão ativos e prontos para vender.'
              : 'Publique o produto para ativar os links de compra abaixo.'}
          </p>
        </div>
        <div className={actionRow}>
          <StatusBadge
            tone={presentation.tone}
            label={presentation.label}
            icon={<StatusIcon size={13} />}
            size="sm"
          />
          <Toggle
            checked={published}
            onChange={controls.onSetPublished}
            disabled={controls.isPending}
            ariaLabel="Publicar produto"
          />
        </div>
      </div>
    </div>
  )
}

function renderLinks(
  product: Product,
  controls: PublishControls,
  openBuilder: (productId: string, checkoutId?: string) => void,
) {
  const offerById = new Map(product.offers.map((offer) => [offer.id, offer]))
  const primaryOfferId = primaryOfferIdFor(product)
  const extraOfferLinks =
    MAX_OFFERS_PER_PRODUCT > 1
      ? product.links.offers.filter((offer) => offer.offerId !== primaryOfferId)
      : []
  const affiliateEnabled = product.affiliate.enabled
  const commission = formatPercentageLabel(parseDecimal(product.affiliate.commission))
  const published = product.status === 'ACTIVE'
  const primaryCheckout = product.checkouts[0]
  return (
    <Card className={section}>
      <div>
        <h2 className={sectionTitle}>{MAX_OFFERS_PER_PRODUCT > 1 ? 'Links & ofertas' : 'Links'}</h2>
        <p className={sectionCopy}>
          Cada link vem de uma parte do produto e carrega uma informação diferente. QR Code fica
          como stub neste F8.
        </p>
      </div>

      <ProductPublishCard product={product} controls={controls} />

      <div className={sectionHeader}>
        <div>
          <h3 className={sectionTitle}>Vender</h3>
          <p className={sectionCopy}>
            Use os checkouts configurados como links de pagamento e ajuste a experiência visual no
            editor.
          </p>
        </div>
        {primaryCheckout ? (
          <Button
            leftIcon={<Paintbrush size={16} />}
            onClick={() => openBuilder(product.id, primaryCheckout.id)}
          >
            Estilo do Checkout
          </Button>
        ) : null}
      </div>

      {product.checkouts.length > 0 ? (
        <div className={tableLike}>
          {product.checkouts.map((checkout) => (
            <CheckoutLinkSummary
              key={checkout.id}
              checkout={checkout}
              product={product}
              published={published}
            />
          ))}
        </div>
      ) : (
        <div className={compactCard}>
          <span className={nodeTitle}>Nenhum checkout configurado</span>
          <p className={muted}>Crie um checkout para liberar links dedicados de pagamento.</p>
        </div>
      )}

      {extraOfferLinks.map((offer) => {
        return (
          <OfferLinkSummary
            key={offer.offerId}
            offerLink={offer}
            offer={offerById.get(offer.offerId)}
            published={published}
          />
        )
      })}

      <div>
        <h3 className={sectionTitle}>Crescer com afiliados</h3>
        <p className={sectionCopy}>Link de recrutamento: não é link de compra.</p>
      </div>

      <CopyRow
        label="Convite de afiliado"
        value={product.links.affiliateInvite}
        hint={
          affiliateEnabled
            ? `Recrutamento de afiliados · comissão ${commission}`
            : 'Ative Afiliados em Recursos para usar este convite.'
        }
      />

      <Button variant="secondary">QR Code indisponível por enquanto</Button>

      {product.links.snippets.length > 0 ? (
        <div>
          <h3 className={sectionTitle}>Snippets do funil</h3>
          <p className={sectionCopy}>Cole no seu site para embutir uma etapa do funil.</p>
        </div>
      ) : null}
      {product.links.snippets.map((snippet) => (
        <div key={snippet.nodeId} className={compactCard}>
          <span className={nodeTitle}>{snippet.title}</span>
          <code className={codeBox}>{snippet.code}</code>
        </div>
      ))}
    </Card>
  )
}

function copyLink(value: string) {
  if (!value) return
  void navigator.clipboard?.writeText(value)
  toast.success('Link copiado.')
}

function CheckoutLinkSummary({
  checkout,
  product,
  published,
}: {
  checkout: Product['checkouts'][number]
  product: Product
  published: boolean
}) {
  const linkAvailable = published && Boolean(checkout.link)
  const link = linkAvailable ? checkout.link : ''
  const linkLabel = published
    ? checkout.link || 'Link gerado ao salvar o produto.'
    : 'Publique o produto para liberar este link.'
  const content = (
    <>
      <div className={checkoutSummaryHeader}>
        <div>
          <span className={nodeTitle}>{checkout.name}</span>
          <p className={muted}>{linkLabel}</p>
          <p className={muted}>Cobra: oferta principal · {formatBuyerPrice(product.price)}</p>
        </div>
        <span className={badge}>
          {checkoutStatusLabels[published ? checkout.status : 'INACTIVE']}
        </span>
      </div>
    </>
  )

  return (
    <div className={checkoutSummaryCard}>
      {link ? (
        <a
          className={checkoutSummaryLink}
          href={link}
          target="_blank"
          rel="noreferrer"
          aria-label={`Abrir ${checkout.name}`}
        >
          {content}
        </a>
      ) : (
        <div className={checkoutSummaryContent}>{content}</div>
      )}
      <Button
        variant="secondary"
        leftIcon={<Copy size={16} />}
        disabled={!link}
        onClick={() => copyLink(link)}
      >
        Copiar
      </Button>
    </div>
  )
}

function OfferLinkSummary({
  offerLink,
  offer,
  published,
}: {
  offerLink: Product['links']['offers'][number]
  offer: Product['offers'][number] | undefined
  published: boolean
}) {
  const status = offer?.status ?? 'ACTIVE'
  const linkAvailable = published && status === 'ACTIVE' && Boolean(offerLink.link)
  const link = linkAvailable ? offerLink.link : ''
  const linkLabel = published
    ? offerLink.link || 'Link gerado ao salvar o produto.'
    : 'Publique o produto para liberar este link.'
  const priceLabel = offer
    ? `Cobra: ${offerLink.name} · ${formatBuyerPrice(offer.price)}`
    : 'Cobra esta oferta do produto.'
  const content = (
    <>
      <div className={checkoutSummaryHeader}>
        <div>
          <span className={nodeTitle}>Oferta: {offerLink.name}</span>
          <p className={muted}>{linkLabel}</p>
          <p className={muted}>{priceLabel}</p>
        </div>
        <span className={badge}>{checkoutStatusLabels[status]}</span>
      </div>
    </>
  )

  return (
    <div className={checkoutSummaryCard}>
      {link ? (
        <a
          className={checkoutSummaryLink}
          href={link}
          target="_blank"
          rel="noreferrer"
          aria-label={`Abrir ${offerLink.name}`}
        >
          {content}
        </a>
      ) : (
        <div className={checkoutSummaryContent}>{content}</div>
      )}
      <Button
        variant="secondary"
        leftIcon={<Copy size={16} />}
        disabled={!link}
        onClick={() => copyLink(link)}
      >
        Copiar
      </Button>
    </div>
  )
}

function CopyRow({
  label,
  value,
  hint,
  warning,
}: {
  label: string
  value: string
  hint?: string
  warning?: string
}) {
  const content = (
    <>
      <span className={nodeTitle}>{label}</span>
      <p className={muted}>{value}</p>
      {hint ? <p className={muted}>{hint}</p> : null}
      {warning ? <p className={muted}>Atenção: {warning}</p> : null}
    </>
  )

  return (
    <div className={rowCard}>
      {value ? (
        <a
          className={linkCardMain}
          href={value}
          target="_blank"
          rel="noreferrer"
          aria-label={`Abrir ${label}`}
        >
          {content}
        </a>
      ) : (
        <div className={linkCardContent}>{content}</div>
      )}
      <Button
        variant="secondary"
        leftIcon={<Copy size={16} />}
        disabled={!value}
        onClick={() => copyLink(value)}
      >
        Copiar
      </Button>
    </div>
  )
}
